import { useCallback, useEffect, useRef, useState, type SyntheticEvent } from 'react';
import { Link, useBlocker, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ModalSheet } from './ui.js';
import { ChatInterpretation } from './chat-interpretation.js';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api, ApiClientError, setSessionToken } from './api.js';
import { DateWheel } from './date-wheel.js';
import {
  readAppearancePreference,
  saveAppearancePreference,
  type AppearancePreference,
} from './appearance.js';
import { unappealedActions, type AccountSanction } from './account-appeal.js';
import { authenticateTelegram, logoutTelegram } from './auth-client.js';
import {
  chatAiRequest,
  chatAiSuggestions,
  chatAiToneLabels,
  type ChatAiResult,
  type ChatAiTask,
  type ChatAiTone,
} from './chat-ai.js';
import { shouldSubmitChatComposer } from './chat-composer.js';
import { ChatRetentionNotice, type RetentionNotice } from './chat-retention-notice.js';
import {
  clearConversationUnread,
  lastConfirmedSeq,
  mergeChatTimeline,
  recoverChatTimeline,
  type ChatTimeline,
  type ChatTimelineItem,
} from './chat-sync.js';
import { isChatNearLatest, restoreChatAfterPrepend, scrollChatToLatest } from './chat-scroll.js';
import { getChatState, updateChatDraft, updateChatScroll } from './chat-state.js';
import { shouldAcknowledgeChatRead } from './chat-receipt.js';
import {
  ChatRateLimitNotice,
  isRequestRateLimited,
  preserveChatOnRateLimit,
} from './chat-rate-limit.js';
import { shouldAutoOpenCurrentGame } from './chat-game-route.js';
import {
  gameAnswerLabel,
  gameHistoryTitle,
  gameInviteBody,
  gamePromptText,
  gameQuickAnswers,
  gameRoundLimit,
  gameStatusText,
  gameTitle,
  guessFactRoleText,
  type ChatGame,
  type GameCatalogEntry,
} from './chat-games.js';
import {
  buildReportRequest,
  buildUnmatchRequest,
  reportCategories,
  type ReportCategory,
} from './chat-safety.js';
import { discoveryRefreshInterval, preservedDiscoveryIndex } from './discovery-refresh.js';
import {
  chatItemText,
  chatItemTime,
  deletedConversationName,
  isUnavailableConversation,
} from './chat-presentation.js';
import { incomingLikesEmptyDescription } from './social-copy.js';
import { photoUrlsToPreload, preloadImages } from './image-preload.js';
import { hydratePublicProfilePhotos } from './public-profile-media.js';
import {
  clearLocalAcceptanceSessionQueries,
  clearMatchedProfileCaches,
  clearPostOnboardingQueries,
  withDiscoveryProfileAt,
  withoutDiscoveryProfile,
} from './social-cache.js';
import { getTelegram } from './telegram.js';
import { FlirtConsent, flirtWarning } from './flirt-consent.js';
import { useLegalManifest, acceptancePayload, LegalDocumentBody } from './legal-consent.js';
import { LocalAcceptanceLogin, localAcceptanceEnabled } from './local-acceptance.js';
import { initialSeekingValue, searchCityOptions } from './onboarding-state.js';
import {
  canRemoveOwnerPhoto,
  MAX_PROFILE_PHOTOS,
  MAX_UPLOAD_BYTES,
  optimizeProfilePhoto,
  ownerPhotoPreviewSrc,
  profilePhotoDimensions,
  isDuplicatePhotoError,
  runPhotoUploadQueue,
  terminalPhotoCollectionStatus,
  terminalPhotoStatusMessage,
  type OwnerPhotoPreview,
} from './profile-photos.js';
import { resolveGallerySelection, type GallerySelection } from './photo-gallery.js';
import { useSession } from './session.js';
import { typologyDeepLinkRoute } from './typology-share.js';
import { clearLocalAcceptanceSession } from './local-acceptance-session.js';
import {
  AsyncState,
  Avatar,
  Button,
  Card,
  IconButton,
  Input,
  Screen,
  Select,
  Textarea,
  Toggle,
} from './ui.js';

export async function notificationEntryRoute(fallback: string, token: string | null) {
  if (!token) return fallback;
  try {
    return (await api<{ path: string }>(`/notification-links/${encodeURIComponent(token)}`)).path;
  } catch {
    return fallback;
  }
}

export function BootPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setSession = useSession((state) => state.setSession);
  const [error, setError] = useState<ApiClientError | null>(null);
  const telegram = getTelegram();
  const initData =
    telegram?.initData || (import.meta.env.DEV ? import.meta.env.VITE_LOCAL_INIT_DATA : '');
  const notificationToken = new URLSearchParams(window.location.search).get('notification');
  useEffect(() => {
    if (!initData) return;
    void authenticateTelegram(initData)
      .then(async (result) => {
        setSession(result.user);
        const route = await notificationEntryRoute(result.nextRoute, notificationToken);
        void navigate(typologyDeepLinkRoute(telegram?.initDataUnsafe?.start_param, route), {
          replace: true,
        });
      })
      .catch((reason: unknown) => {
        setError(
          reason instanceof ApiClientError
            ? reason
            : new ApiClientError('NETWORK_ERROR', 'Нет соединения'),
        );
      });
  }, [initData, navigate, notificationToken, setSession, telegram]);
  if (!initData && localAcceptanceEnabled())
    return (
      <LocalAcceptanceLogin
        onAuthenticated={(result) => {
          clearLocalAcceptanceSessionQueries(queryClient);
          setSession(result.user);
          void notificationEntryRoute(result.nextRoute, notificationToken).then((path) =>
            navigate(path, { replace: true }),
          );
        }}
      />
    );
  if (!initData)
    return (
      <Screen title="Откройте PONCHIK в Telegram" eyebrow="Безопасный вход">
        <p className="lead">Авторизация работает только через официальный Telegram Mini App.</p>
        <Button
          onClick={() => telegram?.openTelegramLink('https://t.me/replace_bot_username?startapp')}
        >
          Открыть в Telegram
        </Button>
      </Screen>
    );
  if (error)
    return (
      <Screen title="Не удалось войти">
        <AsyncState kind="error" {...(error.requestId ? { requestId: error.requestId } : {})} />
        <Button
          onClick={() => {
            window.location.reload();
          }}
        >
          Повторить
        </Button>
      </Screen>
    );
  return (
    <Screen title="Готовим знакомства" eyebrow="PONCHIK">
      <div className="hero-mark" aria-hidden="true">
        P
      </div>
      <AsyncState kind="loading" />
    </Screen>
  );
}

export function WelcomePage() {
  const navigate = useNavigate();
  const manifest = useLegalManifest();
  const [legalIdempotencyKey] = useState(() => crypto.randomUUID());
  const acceptLegal = useMutation({
    mutationFn: () =>
      api('/me/onboarding/accept-legal', {
        method: 'POST',
        body: JSON.stringify(acceptancePayload(manifest.data!, legalIdempotencyKey)),
      }),
    onSuccess: () => navigate('/onboarding/basics'),
    onError: () => {
      void manifest.refetch();
    },
  });
  return (
    <Screen title="PONCHIK" className="screen--welcome" header={<></>}>
      <section className="welcome-hero">
        <div className="mascot-slot" role="img" aria-label="Маскот PONCHIK" />
        <p className="welcome-hero__brand">PONCHIK</p>
        <h1>Всегда есть о чём поговорить.</h1>
        <p className="lead">AI поможет начать, продолжить и оживить переписку.</p>
      </section>
      {manifest.isError && (
        <p role="alert">Документы недоступны. Начало регистрации заблокировано.</p>
      )}
      {acceptLegal.isError && (
        <p role="alert">
          Не удалось сохранить согласие. Откройте актуальные документы и повторите.
        </p>
      )}
      <Button
        type="button"
        onClick={() => acceptLegal.mutate()}
        disabled={acceptLegal.isPending || !manifest.data || manifest.isError}
      >
        {acceptLegal.isPending ? 'Открываем…' : 'Начать знакомства'}
      </Button>
      <p className="legal-links legal-links--compact">
        Продолжая, вы принимаете{' '}
        <Link
          to={manifest.data?.documents.find((doc) => doc.type === 'terms')?.url ?? '/legal/terms'}
        >
          Условия
        </Link>{' '}
        и{' '}
        <Link
          to={
            manifest.data?.documents.find((doc) => doc.type === 'privacy')?.url ?? '/legal/privacy'
          }
        >
          Политику конфиденциальности
        </Link>
        .
      </p>
      <small className="hint">18+</small>
    </Screen>
  );
}

const steps = ['basics', 'preferences', 'location', 'photos'] as const;

export function OnboardingPage() {
  const { step = 'basics' } = useParams();
  const navigate = useNavigate();
  const index = Math.max(0, steps.indexOf(step as (typeof steps)[number]));
  const [message, setMessage] = useState('');
  const [version, setVersion] = useState(0);
  const [birthDate, setBirthDate] = useState('');
  useEffect(() => {
    setMessage('');
  }, [step]);
  const currentProfile = useQuery({
    queryKey: ['onboarding-profile'],
    queryFn: () =>
      api<{
        version: number;
        birthDate: string;
        onboardingStep: (typeof steps)[number] | null;
        public?: {
          displayName: string;
          gender: string;
          cityId?: string | null;
          countryCode?: string | null;
          goal?: string | null;
        };
        preferences?: {
          seekingGenders?: string[];
          goal?: string | null;
          cityId?: string | null;
        } | null;
      }>('/me/profile'),
    retry: false,
  });
  useEffect(() => {
    if (!currentProfile.data) return;
    setVersion(currentProfile.data.version);
    setBirthDate(currentProfile.data.birthDate);
    if (currentProfile.data.onboardingStep && currentProfile.data.onboardingStep !== step)
      void navigate(`/onboarding/${currentProfile.data.onboardingStep}`, { replace: true });
  }, [currentProfile.data, navigate, step]);
  const mutation = useMutation({
    mutationFn: async (form: HTMLFormElement) => {
      const formData = new FormData(form);
      const values = Object.fromEntries(formData.entries());
      if (step === 'basics')
        return api<{ version: number }>('/me/profile/basics', {
          method: 'PUT',
          body: JSON.stringify({
            displayName: values.displayName,
            birthDate: values.birthDate,
            gender: values.gender || undefined,
            version,
          }),
        });
      if (step === 'preferences')
        return api<{ version: number }>('/me/profile/preferences', {
          method: 'PUT',
          body: JSON.stringify({
            seeking: values.seeking === 'all' ? ['woman', 'man'] : [values.seeking],
            goal: values.goal || null,
            ageMin: 18,
            ageMax: 99,
            regionFallback: false,
            introEnabled: true,
            version,
          }),
        });
      if (step === 'location')
        return api<{ version: number }>('/me/profile/location', {
          method: 'PUT',
          body: JSON.stringify({ cityId: values.cityId, countryCode: values.countryCode, version }),
        });
      throw new ApiClientError(
        'REQUEST_INVALID',
        'Сначала добавьте фотографии через безопасную загрузку ниже',
      );
    },
    onSuccess: async (result) => {
      setMessage('');
      if (
        result &&
        typeof result === 'object' &&
        'version' in result &&
        typeof result.version === 'number'
      )
        setVersion(result.version);
      if (result && typeof result === 'object' && 'state' in result && result.state === 'ACTIVE') {
        void navigate('/discover');
        return;
      }
      // The next step derives its initial values from the persisted server
      // draft. Rehydrate before navigation so a same-component route change
      // cannot render against the pre-save profile snapshot.
      await currentProfile.refetch();
      const next = steps[index + 1];
      if (next) void navigate(`/onboarding/${next}`);
      else void navigate('/account/status');
    },
    onError: (reason) => {
      setMessage(reason instanceof Error ? reason.message : 'Проверьте данные');
    },
  });
  const onSubmit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage('');
    mutation.mutate(event.currentTarget);
  };
  return (
    <Screen
      title={onboardingTitle(step)}
      eyebrow={`Шаг ${index + 1} из ${steps.length}`}
      className="screen--onboarding"
    >
      <p className="onboarding-intro">{onboardingIntro(step)}</p>
      <div
        className="progress"
        role="progressbar"
        aria-label="Прогресс заполнения анкеты"
        aria-valuemin={1}
        aria-valuemax={steps.length}
        aria-valuenow={index + 1}
      >
        <span style={{ width: `${((index + 1) / steps.length) * 100}%` }} />
      </div>
      <form className="form" onSubmit={onSubmit} key={currentProfile.data?.version ?? 'loading'}>
        <input type="hidden" name="version" value={version} />
        {step === 'basics' && (
          <>
            <Field label="Как вас называть?">
              <input
                name="displayName"
                minLength={2}
                maxLength={40}
                required
                autoComplete="name"
                defaultValue={currentProfile.data?.public?.displayName ?? ''}
              />
            </Field>
            <Field label="Дата рождения">
              <DateWheel value={birthDate} onChange={setBirthDate} />
            </Field>
            <ChoiceCards
              label="Пол"
              name="gender"
              defaultValue={currentProfile.data?.public?.gender ?? 'prefer_not_to_say'}
              options={[
                { value: 'woman', label: 'Женщина', icon: 'woman' },
                { value: 'man', label: 'Мужчина', icon: 'man' },
                { value: 'prefer_not_to_say', label: 'Не указывать', icon: 'private' },
              ]}
            />
          </>
        )}
        {step === 'preferences' && (
          <>
            <ChoiceCards
              label="Кого вы хотите видеть?"
              name="seeking"
              defaultValue={initialSeekingValue(
                currentProfile.data?.public?.gender,
                currentProfile.data?.preferences?.seekingGenders,
              )}
              options={[
                { value: 'woman', label: 'Женщин', icon: 'woman' },
                { value: 'man', label: 'Мужчин', icon: 'man' },
                { value: 'all', label: 'Всех', icon: 'all' },
              ]}
            />
            <Field label="Цель">
              <select
                name="goal"
                defaultValue={
                  currentProfile.data?.preferences?.goal ?? currentProfile.data?.public?.goal ?? ''
                }
              >
                <option value="">Не указывать</option>
                <option value="relationship">Отношения</option>
                <option value="communication">Общение</option>
                <option value="flirt">Флирт</option>
                <option value="unsure">Пока не знаю</option>
              </select>
            </Field>
          </>
        )}
        {step === 'location' && (
          <>
            <CityField
              initialCountry={currentProfile.data?.public?.countryCode}
              initialValue={currentProfile.data?.public?.cityId}
            />
          </>
        )}
        {step === 'photos' && (
          <PhotoStep
            version={version}
            onComplete={() => {
              void navigate('/discover');
            }}
          />
        )}
        {message && (
          <p className="form-error" role="alert">
            {message}
          </p>
        )}
        {step !== 'photos' && (
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Сохраняем…' : 'Продолжить'}
          </Button>
        )}
      </form>
    </Screen>
  );
}

function onboardingTitle(step: string) {
  return (
    (
      {
        basics: 'Расскажите о себе',
        preferences: 'Кого вы ищете',
        location: 'Ваш город',
        photos: 'Добавьте фотографии',
      } as Record<string, string>
    )[step] ?? 'Редактирование профиля'
  );
}

function onboardingIntro(step: string) {
  return (
    (
      {
        basics: 'Немного о вас — это займёт меньше минуты.',
        preferences: 'Настройте ленту так, чтобы видеть близких по духу людей.',
        location: 'Покажем анкеты рядом с выбранным городом.',
        photos: 'Добавьте до трёх фото. Первое готовое станет главным.',
      } as Record<string, string>
    )[step] ?? ''
  );
}

type ChoiceIcon = 'woman' | 'man' | 'all' | 'private';

function ChoiceSymbol({ name }: { name: ChoiceIcon }) {
  return (
    <svg className="choice-card__icon" viewBox="0 0 24 24" aria-hidden="true">
      {name === 'woman' && (
        <>
          <circle cx="12" cy="8" r="5" />
          <path d="M12 13v8m-3-3h6" />
        </>
      )}
      {name === 'man' && (
        <>
          <circle cx="9" cy="15" r="5" />
          <path d="m13 11 7-7m-5 0h5v5" />
        </>
      )}
      {name === 'all' && (
        <>
          <circle cx="9" cy="10" r="4" />
          <circle cx="16" cy="9" r="3" />
          <path d="M2.5 21a6.5 6.5 0 0 1 13 0m-1.5-5a5 5 0 0 1 7.5 4.3" />
        </>
      )}
      {name === 'private' && (
        <>
          <path d="M4 12s3-5 8-5 8 5 8 5-3 5-8 5-8-5-8-5Z" />
          <path d="m5 4 14 16" />
        </>
      )}
    </svg>
  );
}

function ChoiceCards({
  label,
  name,
  defaultValue,
  options,
}: {
  label: string;
  name: string;
  defaultValue: string;
  options: Array<{ value: string; label: string; icon: ChoiceIcon }>;
}) {
  return (
    <fieldset className="choice-field">
      <legend>{label}</legend>
      <div className="choice-cards">
        {options.map((option) => (
          <label className="choice-card" key={option.value}>
            <input
              type="radio"
              name={name}
              value={option.value}
              defaultChecked={defaultValue === option.value}
              required
            />
            <span>
              <ChoiceSymbol name={option.icon} />
              <b>{option.label}</b>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}

function CityField({
  initialValue,
  initialCountry,
}: {
  initialValue?: string | null | undefined;
  initialCountry?: string | null | undefined;
}) {
  const [value, setValue] = useState(initialValue ?? '');
  const [countryCode, setCountryCode] = useState(initialCountry ?? '');
  useEffect(() => setValue(initialValue ?? ''), [initialValue]);
  useEffect(() => setCountryCode(initialCountry ?? ''), [initialCountry]);
  const cities = useQuery({
    queryKey: ['cities'],
    queryFn: () => api<CityOption[]>('/reference/cities'),
  });
  return (
    <CityPicker
      required
      value={value}
      onChange={setValue}
      countryCode={countryCode}
      onCountryChange={(code) => {
        setCountryCode(code);
        setValue('');
      }}
      cities={cities.data ?? []}
      loading={cities.isLoading}
    />
  );
}

type CityOption = {
  id: string;
  nameRu: string;
  region: string;
  countryCode: string;
  browseRegion?: string;
  districtName?: string;
};
export function CityPicker({
  cities,
  value,
  onChange,
  required = false,
  loading = false,
  countryCode,
  onCountryChange,
  countryLabel = 'Страна проживания',
  cityLabel = 'Город проживания',
}: {
  cities: CityOption[];
  value?: string | null;
  onChange?: (cityId: string) => void;
  required?: boolean;
  loading?: boolean;
  countryCode: string;
  onCountryChange: (code: string) => void;
  countryLabel?: string;
  cityLabel?: string;
}) {
  const countries = useQuery({
    queryKey: ['countries'],
    queryFn: () => api<Array<{ code: string; nameRu: string }>>('/reference/countries'),
  });
  const [query, setQuery] = useState('');
  const [resultsOpen, setResultsOpen] = useState(true);
  const selected = cities.find((city) => city.id === value && city.countryCode === countryCode);
  const shown = searchCityOptions(
    cities.filter((city) => city.countryCode === countryCode),
    query,
  );
  return (
    <>
      <Field label={countryLabel}>
        <select
          name="countryCode"
          value={countryCode}
          required={required}
          disabled={countries.isLoading || countries.isError}
          onChange={(event) => {
            setQuery('');
            setResultsOpen(true);
            onCountryChange(event.target.value);
          }}
        >
          <option value="">Выберите страну</option>
          {countries.data?.map((country) => (
            <option key={country.code} value={country.code}>
              {country.nameRu}
            </option>
          ))}
        </select>
        {countries.isError && (
          <span className="form-error">Не удалось загрузить страны. Обновите страницу.</span>
        )}
      </Field>
      <Field label={cityLabel}>
        <input
          type="hidden"
          name="cityId"
          value={selected?.id ?? ''}
          required={required}
          readOnly
        />
        <span className="city-search">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-4-4" />
          </svg>
          <input
            aria-label="Найти город"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setResultsOpen(true);
            }}
            placeholder="Введите город"
            disabled={loading || !countryCode}
          />
        </span>
        {resultsOpen && (
          <div className="city-picker" aria-live="polite" aria-busy={loading}>
            {loading && <p className="city-picker__state">Загружаем города…</p>}
            {!countryCode && <p className="city-picker__state">Сначала выберите страну.</p>}
            {!loading && query.trim() && shown.length === 0 && (
              <p className="city-picker__state">Город не найден. Проверьте написание.</p>
            )}
            {shown.map((city) => (
              <button
                type="button"
                className={city.id === value ? 'is-selected' : ''}
                key={city.id}
                onClick={() => {
                  onChange?.(city.id);
                  setResultsOpen(false);
                }}
              >
                {city.nameRu}
                <small>{city.region}</small>
              </button>
            ))}
          </div>
        )}
        {selected && (
          <small className="city-selected">
            <span aria-hidden="true">✓</span> Выбрано: {selected.nameRu}
          </small>
        )}
      </Field>
    </>
  );
}

function PhotoStep({
  version,
  onComplete,
  manageOnly = false,
}: {
  version: number;
  onComplete: () => void;
  manageOnly?: boolean;
}) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('Добавьте хотя бы одну фотографию.');
  type PendingPhoto = {
    id: string;
    assetId?: string;
    preview: string;
    source: File;
    state: 'UPLOADING' | 'FAILED';
    replacePhotoId?: string;
    prepared?: {
      assetId: string;
      file: File;
    };
  };
  const [pending, setPending] = useState<PendingPhoto[]>([]);
  const photos = useQuery({
    queryKey: ['onboarding-photos'],
    queryFn: () => api<OwnerPhoto[]>('/me/photos'),
    refetchInterval: (query) =>
      query.state.data?.some((photo) =>
        ['UPLOADING', 'UPLOADED', 'PROCESSING'].includes(photo.status),
      )
        ? 2_000
        : false,
  });
  const remove = useMutation({
    mutationFn: (photoId: string) => api(`/me/photos/${photoId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await photos.refetch();
      await invalidateOwnerProfileQueries(queryClient);
    },
    onError: () => setStatus('Не удалось удалить фото. Повторите попытку.'),
  });
  const reorder = useMutation({
    mutationFn: (primaryId: string) => {
      const assetIds = (photos.data ?? []).map((photo) => photo.assetId);
      return api('/me/photos/order', {
        method: 'PATCH',
        body: JSON.stringify({ assetIds, primaryId }),
      });
    },
    onSuccess: async () => {
      await photos.refetch();
      await invalidateOwnerProfileQueries(queryClient);
    },
    onError: () => setStatus('Не удалось изменить главное фото.'),
  });
  useEffect(() => {
    setPending((value) =>
      value
        .filter(
          (item) =>
            !item.assetId ||
            !photos.data?.some(
              (photo) => photo.assetId === item.assetId && photo.status === 'READY',
            ),
        )
        .map((item) => {
          if (
            !item.assetId ||
            !photos.data?.some(
              (photo) => photo.assetId === item.assetId && photo.status === 'FAILED',
            )
          )
            return item;
          const retryable = { ...item };
          delete retryable.prepared;
          return { ...retryable, state: 'FAILED' as const };
        }),
    );
  }, [photos.data]);
  useEffect(() => {
    const terminalStatus = terminalPhotoCollectionStatus(
      pending.map((photo) => photo.state),
      photos.data?.map((photo) => photo.status) ?? [],
    );
    if (terminalStatus === 'FAILED')
      setStatus((current) => terminalPhotoStatusMessage(current, terminalStatus));
    if (terminalStatus === 'READY') {
      setStatus((current) => terminalPhotoStatusMessage(current, terminalStatus));
      void invalidateOwnerProfileQueries(queryClient);
    }
  }, [pending, photos.data, queryClient]);
  const submit = useMutation({
    mutationFn: () =>
      api('/me/profile/submit', {
        method: 'POST',
        body: JSON.stringify({ version }),
      }),
    onSuccess: async () => {
      clearPostOnboardingQueries(queryClient);
      await invalidateOwnerProfileQueries(queryClient);
      onComplete();
    },
    onError: () => setStatus('Добавьте готовое фото и проверьте обязательные поля.'),
  });
  const upload = async (pendingPhoto: PendingPhoto) => {
    const { id, source } = pendingPhoto;
    setPending((value) =>
      value.map((item) => (item.id === id ? { ...item, state: 'UPLOADING' } : item)),
    );
    try {
      setStatus('Загружаем…');
      let prepared = pendingPhoto.prepared;
      if (!prepared) {
        const file = await optimizeProfilePhoto(source);
        const [digest, dimensions] = await Promise.all([
          crypto.subtle.digest('SHA-256', await file.arrayBuffer()),
          profilePhotoDimensions(file),
        ]);
        const sha256 = [...new Uint8Array(digest)]
          .map((value) => value.toString(16).padStart(2, '0'))
          .join('');
        const intent = await api<{
          assetId: string;
        }>('/me/photos/upload-intents', {
          method: 'POST',
          body: JSON.stringify({
            mime: file.type,
            bytes: file.size,
            width: dimensions.width,
            height: dimensions.height,
            sha256,
            ...(pendingPhoto.replacePhotoId ? { replacePhotoId: pendingPhoto.replacePhotoId } : {}),
          }),
        });
        const nextPrepared = { ...intent, file };
        prepared = nextPrepared;
        setPending((value) =>
          value.map((item) =>
            item.id === id ? { ...item, assetId: intent.assetId, prepared: nextPrepared } : item,
          ),
        );
      }
      const uploaded = await api<{ etag?: string }>(`/me/photos/${prepared.assetId}/upload`, {
        method: 'PUT',
        body: prepared.file,
        headers: { 'Content-Type': prepared.file.type },
      });
      await api(`/me/photos/${prepared.assetId}/complete`, {
        method: 'POST',
        body: JSON.stringify({
          size: prepared.file.size,
          etag: uploaded.etag,
          ...(pendingPhoto.replacePhotoId ? { replacePhotoId: pendingPhoto.replacePhotoId } : {}),
        }),
      });
      setStatus('Обрабатываем…');
      await photos.refetch();
    } catch (error) {
      if (isDuplicatePhotoError(error)) {
        URL.revokeObjectURL(pendingPhoto.preview);
        setPending((value) => value.filter((item) => item.id !== id));
        setStatus('Эта фотография уже добавлена');
        return;
      }
      setPending((value) =>
        value.map((item) => (item.id === id ? { ...item, state: 'FAILED' } : item)),
      );
      setStatus('Не удалось обработать фото. Повторите попытку.');
    }
  };
  const queueFiles = (files: readonly File[], replacePhoto?: OwnerPhoto) => {
    const available = replacePhoto
      ? 1
      : MAX_PROFILE_PHOTOS - (photos.data?.length ?? 0) - pending.length;
    if (files.length > available)
      setStatus(`Можно добавить не больше ${MAX_PROFILE_PHOTOS} фотографий`);
    const selected = files.slice(0, available).filter((file) => {
      if (file.size <= MAX_UPLOAD_BYTES) return true;
      setStatus('Файл больше 10 МБ. Выберите другое фото.');
      return false;
    });
    const queued = selected.map((source) => ({
      id: crypto.randomUUID(),
      preview: URL.createObjectURL(source),
      source,
      state: 'UPLOADING' as const,
      ...(replacePhoto ? { replacePhotoId: replacePhoto.id } : {}),
    }));
    setPending((value) => [...value, ...queued]);
    void runPhotoUploadQueue(queued, upload);
  };
  const discardPending = async (photo: PendingPhoto) => {
    const persisted = photos.data?.find((item) => item.assetId === photo.assetId);
    if (persisted) await api(`/me/photos/${persisted.id}`, { method: 'DELETE' });
    URL.revokeObjectURL(photo.preview);
    setPending((value) => value.filter((item) => item.id !== photo.id));
    await photos.refetch();
  };
  return (
    <>
      <div className="profile-photo-grid">
        {photos.data
          ?.filter(
            (photo) =>
              !pending.some(
                (item) => item.assetId === photo.assetId || item.replacePhotoId === photo.id,
              ),
          )
          .map((photo) => {
            const src = ownerPhotoPreviewSrc(photo.previewUrl, localAcceptanceEnabled());
            return (
              <figure
                className={`profile-photo-tile${photo.isPrimary ? ' profile-photo-tile--main' : ''}`}
                key={photo.id}
              >
                {src ? (
                  <img src={src} alt="Выбранная фотография" />
                ) : (
                  <div className="profile-photo-placeholder">
                    {photo.status === 'FAILED'
                      ? 'Не удалось обработать фото'
                      : photo.status === 'READY'
                        ? 'Не удалось показать фото'
                        : 'Обрабатываем…'}
                  </div>
                )}
                <figcaption>
                  {photo.isPrimary ? 'Главная' : `Фото ${photo.position + 1}`}
                </figcaption>
                <div className="profile-photo-actions">
                  {!photo.isPrimary && photo.status === 'READY' && (
                    <button
                      className="profile-photo-action"
                      type="button"
                      disabled={reorder.isPending}
                      onClick={() => reorder.mutate(photo.assetId)}
                    >
                      Сделать главной
                    </button>
                  )}
                  <label className="profile-photo-action">
                    Заменить
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      hidden
                      onChange={(event) => {
                        queueFiles([...(event.target.files ?? [])], photo);
                        event.currentTarget.value = '';
                      }}
                    />
                  </label>
                  {canRemoveOwnerPhoto(photo.status) && (
                    <button
                      className="profile-photo-action profile-photo-action--danger"
                      type="button"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (window.confirm('Удалить эту фотографию из анкеты?'))
                          remove.mutate(photo.id);
                      }}
                    >
                      Удалить
                    </button>
                  )}
                </div>
              </figure>
            );
          })}
        {pending.map((photo, index) => (
          <figure className="profile-photo-tile profile-photo-tile--pending" key={photo.id}>
            <img src={photo.preview} alt="Предпросмотр выбранной фотографии" />
            <figcaption>
              {photos.data?.find((item) => item.id === photo.replacePhotoId)?.isPrimary ||
              (photos.data?.length ?? 0) + index === 0
                ? 'Главная'
                : `Фото ${
                    (photos.data?.find((item) => item.id === photo.replacePhotoId)?.position ??
                      (photos.data?.length ?? 0) + index) + 1
                  }`}
            </figcaption>
            <small className={`photo-state photo-state--${photo.state.toLowerCase()}`}>
              {photo.state === 'FAILED'
                ? 'Не удалось загрузить'
                : photo.assetId
                  ? 'Обрабатываем…'
                  : 'Загружаем…'}
            </small>
            {photo.state === 'FAILED' && (
              <>
                <Button type="button" tone="secondary" onClick={() => void upload(photo)}>
                  Повторить
                </Button>
                <Button type="button" tone="secondary" onClick={() => void discardPending(photo)}>
                  Удалить
                </Button>
              </>
            )}
          </figure>
        ))}
      </div>
      {(photos.data?.length ?? 0) + pending.length < MAX_PROFILE_PHOTOS && (
        <label className="upload">
          <span>＋</span>
          <strong>Выбрать фотографию</strong>
          <small>JPEG, PNG или WebP, до 10 МБ</small>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={(event) => {
              const files = [...(event.target.files ?? [])];
              queueFiles(files);
              event.currentTarget.value = '';
            }}
          />
        </label>
      )}
      <p className="hint" role="status">
        {status}
      </p>
      {manageOnly ? (
        <Button type="button" onClick={onComplete}>
          Готово
        </Button>
      ) : (
        <Button
          type="button"
          disabled={
            submit.isPending || !(photos.data ?? []).some((photo) => photo.status === 'READY')
          }
          onClick={() => submit.mutate()}
        >
          {submit.isPending ? 'Завершаем…' : 'Анкета готова'}
        </Button>
      )}
    </>
  );
}

export function PhotoUploadPage() {
  const navigate = useNavigate();
  const profile = useQuery({
    queryKey: ['photo-upload-profile'],
    queryFn: () => api<{ version: number }>('/me/profile'),
  });
  if (profile.isLoading)
    return (
      <Screen title="Добавить фотографии">
        <AsyncState kind="loading" />
      </Screen>
    );
  if (profile.isError)
    return (
      <Screen title="Добавить фотографии">
        <AsyncState kind="error" />
      </Screen>
    );
  return (
    <Screen title="Добавить фотографии" eyebrow="Моя анкета">
      <PhotoStep
        version={profile.data!.version}
        manageOnly
        onComplete={() => {
          void navigate('/profile/photos');
        }}
      />
    </Screen>
  );
}

export function AccountStatusPage() {
  const result = useQuery({
    queryKey: ['me'],
    queryFn: () =>
      api<{ user: { state: string }; profile: { moderationStatus: string } | null }>('/me'),
  });
  const sanctions = useQuery({
    queryKey: ['appealable-actions'],
    queryFn: () => api<{ items: AccountSanction[] }>('/me/appealable-actions'),
  });
  const [receipt, setReceipt] = useState<string | null>(null);
  const [appealError, setAppealError] = useState('');
  const submitAppeal = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    setAppealError('');
    try {
      const response = await api<{ appealId: string }>('/me/appeals', {
        method: 'POST',
        body: JSON.stringify({
          actionId: values.get('actionId'),
          statement: values.get('statement'),
        }),
      });
      setReceipt(response.appealId);
      await sanctions.refetch();
    } catch (reason) {
      setAppealError(reason instanceof Error ? reason.message : 'Не удалось отправить апелляцию');
    }
  };
  const unappealed = unappealedActions(sanctions.data?.items ?? []);
  if (result.isLoading) return <AsyncState kind="loading" />;
  return (
    <Screen title="Состояние аккаунта" eyebrow="Статус">
      <div className="status-card">
        <span className="status-dot" />
        <div>
          <strong>{result.data?.profile?.moderationStatus ?? result.data?.user.state}</strong>
          <p>Мы сообщим результат нейтральным уведомлением, если вы разрешили сообщения от бота.</p>
        </div>
      </div>
      <Link className="button button--secondary" to="/profile">
        Посмотреть анкету
      </Link>
      {sanctions.isLoading ? <AsyncState kind="loading" /> : null}
      {sanctions.data?.items.length ? (
        <section className="settings-section">
          <h2>Решения модерации</h2>
          {sanctions.data.items.map((item) => (
            <div className="notice" key={item.id}>
              <strong>{item.action}</strong>
              <span>Причина: {item.reasonCode}</span>
              <span>
                {item.appeal
                  ? `Апелляция: ${item.appeal.status}`
                  : 'Это решение можно обжаловать один раз.'}
              </span>
            </div>
          ))}
        </section>
      ) : null}
      {receipt ? (
        <div className="notice">
          <strong>Апелляция принята</strong>
          <span>Номер: {receipt}. Результат появится на этом экране.</span>
        </div>
      ) : unappealed.length ? (
        <form className="form" onSubmit={(event) => void submitAppeal(event)}>
          <Field label="Какое решение обжаловать">
            <select name="actionId" required>
              {unappealed.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.action} — {item.reasonCode}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Почему решение нужно пересмотреть">
            <textarea name="statement" minLength={20} maxLength={1000} required rows={6} />
          </Field>
          {appealError ? <p className="form-error">{appealError}</p> : null}
          <Button type="submit">Отправить апелляцию</Button>
        </form>
      ) : null}
      <Link className="button button--ghost" to="/support">
        Нужна помощь
      </Link>
    </Screen>
  );
}

type ProfileCard = {
  id: string;
  displayName: string;
  avatarAssetId?: string | null;
  age: number;
  city: string;
  country?: string;
  countryCode?: string | null;
  goal?: string;
  bio?: string;
  photos?: { assetId?: string; url?: string }[];
  typology?: { type: string; title: string; emoji: string };
  searchExpanded?: boolean;
  details?: {
    work?: string;
    education?: string;
    hobbies?: string;
    pets?: string[];
    favoriteFilm?: string;
    favoriteMusic?: string;
  };
};

function profileInterests(profile: ProfileCard, limit = 4): string[] {
  return (profile.details?.hobbies ?? '')
    .split(/[,;\n·]/)
    .map((value) => value.trim())
    .filter(Boolean)
    .slice(0, limit);
}

function profileGoalLabel(goal: string): string {
  return (
    {
      relationship: 'Отношения',
      communication: 'Общение',
      flirt: 'Флирт',
      unsure: 'Пока не знаю',
    }[goal] ?? goal
  );
}

function DiscoveryActionIcon({ name }: { name: 'pass' | 'profile' | 'like' }) {
  return (
    <svg className="discovery-action__icon" viewBox="0 0 24 24" aria-hidden="true">
      {name === 'pass' && <path d="m7 7 10 10M17 7 7 17" />}
      {name === 'profile' && (
        <>
          <circle cx="12" cy="8" r="4" />
          <path d="M5 21a7 7 0 0 1 14 0" />
        </>
      )}
      {name === 'like' && (
        <path d="M20.8 4.7a5.4 5.4 0 0 0-7.7 0L12 5.8l-1.1-1.1a5.4 5.4 0 0 0-7.7 7.7L12 21l8.8-8.6a5.4 5.4 0 0 0 0-7.7Z" />
      )}
    </svg>
  );
}

export async function invalidateOwnerProfileQueries(queryClient: QueryClient) {
  await Promise.all(
    [
      ['onboarding-profile'],
      ['discovery-eligibility'],
      ['profile'],
      ['profile-edit'],
      ['search-profile'],
      ['photo-upload-profile'],
      ['profile-photos'],
    ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
  );
}

export async function finishProfileEditSave(
  queryClient: QueryClient,
  navigate: (to: string) => void | Promise<void>,
) {
  await invalidateOwnerProfileQueries(queryClient);
  void navigate('/profile');
}

type IncomingLike = {
  reactionId: string;
  profile: ProfileCard;
  intro?: string;
  mutual: boolean;
  conversationId?: string;
};
type PublicProfileContext = {
  source?: 'discover' | 'likes' | 'chat';
  candidateId?: string;
  reactionId?: string;
  likesScrollY?: number;
  conversationId?: string;
  mutual?: boolean;
};
type MatchRouteState = {
  profile?: ProfileCard;
  conversationId?: string;
  returnTo?: '/likes' | '/discover';
  likesScrollY?: number;
};

type PrivateProfile = {
  version: number;
  birthDate?: string | null;
  public?: {
    displayName?: string | null;
    avatarAssetId?: string | null;
    gender?: string | null;
    cityId?: string | null;
    countryCode?: string | null;
    goal?: string | null;
    bio?: string | null;
    work?: string | null;
    education?: string | null;
    hobbies?: string | null;
    pets?: string[];
    favoriteFilm?: string | null;
    favoriteMusic?: string | null;
  };
  preferences?: {
    seekingGenders?: string[];
    ageMin?: number;
    ageMax?: number;
    cityId?: string | null;
    countryCode?: string | null;
    goal?: string | null;
  } | null;
};

async function hydratePublicPhotos(card: ProfileCard): Promise<ProfileCard> {
  return hydratePublicProfilePhotos(card, (assetId) =>
    api<{ url: string }>(`/media/${assetId}/access?size=medium`),
  );
}

function PublicPhoto({
  photoId,
  url,
  alt,
  onRecover,
}: {
  photoId?: string | undefined;
  url: string | undefined;
  alt: string;
  onRecover?: () => Promise<void>;
}) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const [recovering, setRecovering] = useState(false);
  const [renderAttempt, setRenderAttempt] = useState(0);
  const automaticRecoveryUsed = useRef(false);
  const recoveryInFlight = useRef(false);
  const recoveryGeneration = useRef(0);

  useEffect(() => {
    recoveryGeneration.current += 1;
    automaticRecoveryUsed.current = false;
    recoveryInFlight.current = false;
    setFailedUrl(null);
    setRecovering(false);
    setRenderAttempt(0);
    return () => {
      recoveryGeneration.current += 1;
      recoveryInFlight.current = false;
    };
  }, [photoId]);

  const recover = useCallback(async () => {
    if (!onRecover || recoveryInFlight.current) return;
    const generation = recoveryGeneration.current;
    recoveryInFlight.current = true;
    setRecovering(true);
    try {
      await onRecover();
      if (generation !== recoveryGeneration.current) return;
      setFailedUrl(null);
      setRenderAttempt((attempt) => attempt + 1);
    } catch {
      if (generation !== recoveryGeneration.current) return;
      setFailedUrl(url ?? '');
    } finally {
      if (generation === recoveryGeneration.current) {
        recoveryInFlight.current = false;
        setRecovering(false);
      }
    }
  }, [onRecover, url]);

  useEffect(() => {
    if (url || !photoId || !onRecover || automaticRecoveryUsed.current) return;
    automaticRecoveryUsed.current = true;
    void recover();
  }, [onRecover, photoId, recover, url]);

  const handleError = () => {
    if (!onRecover || automaticRecoveryUsed.current) {
      setFailedUrl(url ?? '');
      return;
    }
    automaticRecoveryUsed.current = true;
    void recover();
  };

  if (recovering)
    return (
      <div className="profile-photo__error" role="status">
        Обновляем фото…
      </div>
    );

  if (!url || failedUrl === url)
    return (
      <div className="profile-photo__error" role="group" aria-label="Фото недоступно">
        <span>Не удалось загрузить фото</span>
        {onRecover && (
          <Button
            type="button"
            tone="secondary"
            onClick={() => void recover()}
            aria-label="Повторить загрузку фото"
          >
            Повторить
          </Button>
        )}
      </div>
    );

  return (
    <img
      key={`${photoId ?? 'photo'}:${url}:${renderAttempt}`}
      src={url}
      alt={alt}
      onError={handleError}
    />
  );
}

export const profileSettingsItems = [
  { to: '/profile/photos', label: 'Фотографии' },
  { to: '/settings/notifications', label: 'Уведомления' },
  { to: '/settings/privacy', label: 'Приватность' },
  { to: '/settings/ai', label: 'AI-помощник' },
  { to: '/settings/appearance', label: 'Оформление' },
  { to: '/settings/account', label: 'Аккаунт' },
  { to: '/support', label: 'Помощь' },
] as const;

type ProfileSettingsIconName =
  | 'edit'
  | 'search'
  | 'photos'
  | 'notifications'
  | 'privacy'
  | 'ai'
  | 'appearance'
  | 'account'
  | 'support';

function ProfileSettingsIcon({ name }: { name: ProfileSettingsIconName }) {
  return (
    <svg className="profile-settings-icon" viewBox="0 0 24 24" aria-hidden="true">
      {name === 'edit' && (
        <path d="m4 20 4.2-1 10.4-10.4a2 2 0 0 0-2.8-2.8L5.4 16.2 4 20Zm10.5-13 2.8 2.8" />
      )}
      {name === 'search' && (
        <>
          <circle cx="10.5" cy="10.5" r="6.5" />
          <path d="m15.5 15.5 4.5 4.5" />
        </>
      )}
      {name === 'photos' && (
        <>
          <rect x="3" y="4" width="18" height="16" rx="3" />
          <circle cx="9" cy="9" r="2" />
          <path d="m5 18 4.5-4.5 3.2 3.2 2.1-2.1L19 18" />
        </>
      )}
      {name === 'notifications' && (
        <path d="M18 9a6 6 0 0 0-12 0c0 7-3 7-3 7h18s-3 0-3-7M10 20h4" />
      )}
      {name === 'privacy' && (
        <path d="M12 22s8-3.7 8-10V5l-8-3-8 3v7c0 6.3 8 10 8 10Zm0-14v6m0 3h.01" />
      )}
      {name === 'ai' && (
        <path d="m12 3 1.4 4.1L18 8.5l-4.6 1.4L12 14l-1.4-4.1L6 8.5l4.6-1.4L12 3Zm6 11 .8 2.2L21 17l-2.2.8L18 20l-.8-2.2L15 17l2.2-.8L18 14Z" />
      )}
      {name === 'appearance' && (
        <path d="M12 3a9 9 0 1 0 9 9c0-1.1-.9-2-2-2h-1.6a2 2 0 0 1-2-2V6.5A3.5 3.5 0 0 0 12 3Zm-4 9h.01M10 7h.01M15 6h.01M7 16h.01" />
      )}
      {name === 'account' && (
        <>
          <circle cx="12" cy="8" r="4" />
          <path d="M4.5 21a7.5 7.5 0 0 1 15 0" />
        </>
      )}
      {name === 'support' && (
        <path d="M4 13v-2a8 8 0 0 1 16 0v2M4 13a2 2 0 0 0 2 2h1v-6H6a2 2 0 0 0-2 2m16 2a2 2 0 0 1-2 2h-1V9h1a2 2 0 0 1 2 2v5a4 4 0 0 1-4 4h-3" />
      )}
    </svg>
  );
}

function SettingsLink({
  to,
  icon,
  label,
  value,
}: {
  to: string;
  icon: ProfileSettingsIconName;
  label: string;
  value?: string;
}) {
  return (
    <Link to={to} className="settings-row">
      <span className="settings-row__icon">
        <ProfileSettingsIcon name={icon} />
      </span>
      <span className="settings-row__copy">
        <strong>{label}</strong>
        {value ? <small>{value}</small> : null}
      </span>
      <span className="settings-row__arrow" aria-hidden="true">
        ›
      </span>
    </Link>
  );
}

export function DiscoverPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const eligibility = useQuery({
    queryKey: ['discovery-eligibility'],
    queryFn: () =>
      api<{ isComplete: boolean; onboardingStep: (typeof steps)[number] | null }>('/me/profile'),
    retry: false,
  });
  const query = useQuery({
    queryKey: ['discovery'],
    queryFn: async () => {
      const result = await api<{
        cards: ProfileCard[];
        poolHint: string;
        mode: 'STRICT' | 'EXPANDED';
        canExpand: boolean;
        expandedUntil: string | null;
      }>('/discovery?limit=10');
      const cards = await Promise.all(result.cards.map(hydratePublicPhotos));
      return { ...result, cards };
    },
    refetchOnMount: 'always',
    refetchInterval: () => discoveryRefreshInterval(document.visibilityState),
    refetchIntervalInBackground: false,
  });
  const [index, setIndex] = useState(0);
  const returnedCandidateId = (location.state as { candidateId?: string } | null)?.candidateId;
  const activeCandidateIdRef = useRef<string | undefined>(undefined);
  const restoredIndex = preservedDiscoveryIndex(
    query.data?.cards ?? [],
    index,
    activeCandidateIdRef.current,
    returnedCandidateId,
  );
  const card = query.data?.cards[restoredIndex];
  const [photoIndex, setPhotoIndex] = useState(0);
  // Пока открыта карточка, заранее качаем фото следующих: свайп показывает их сразу.
  useEffect(() => {
    preloadImages(photoUrlsToPreload(query.data?.cards ?? [], restoredIndex));
  }, [query.data?.cards, restoredIndex]);
  const discoveryTouchStart = useRef<number | null>(null);
  useEffect(() => {
    if (card?.id) activeCandidateIdRef.current = card.id;
  }, [card?.id]);
  useEffect(() => setPhotoIndex(0), [card?.id]);
  const [undo, setUndo] = useState<{ card: ProfileCard; index: number } | null>(null);
  const expandMutation = useMutation({
    mutationFn: () => api('/discovery/expand', { method: 'POST' }),
    onSuccess: () => {
      setIndex(0);
      activeCandidateIdRef.current = undefined;
      void queryClient.invalidateQueries({ queryKey: ['discovery'] });
    },
  });
  const mutation = useMutation({
    mutationFn: ({ type, profile }: { type: 'pass' | 'like'; profile: ProfileCard }) =>
      api<{ match?: { conversationId?: string } | null }>(`/profiles/${profile.id}/${type}`, {
        method: 'POST',
        body: JSON.stringify({ source: 'discover' }),
      }),
    onSuccess: (result, { type, profile }) => {
      if (type === 'pass') setUndo({ card: profile, index: restoredIndex });
      else setUndo(null);
      if (type === 'like' && result.match?.conversationId) {
        clearMatchedProfileCaches(queryClient, profile.id);
        void navigate('/match', {
          state: {
            profile,
            conversationId: result.match.conversationId,
            returnTo: '/discover',
          } satisfies MatchRouteState,
        });
        return;
      }
      let remainingCards = 0;
      queryClient.setQueryData(['discovery'], (current: { cards: ProfileCard[] } | undefined) => {
        const next = withoutDiscoveryProfile(current, profile.id);
        remainingCards = next?.cards.length ?? 0;
        return next;
      });
      setIndex(restoredIndex);
      if (remainingCards === 0) void queryClient.invalidateQueries({ queryKey: ['discovery'] });
    },
  });

  const undoMutation = useMutation({
    mutationFn: () =>
      api(`/profiles/${undo!.card.id}/pass/undo`, {
        method: 'POST',
        body: JSON.stringify({ source: 'discover' }),
      }),
    onSuccess: () => {
      if (undo) {
        activeCandidateIdRef.current = undo.card.id;
        queryClient.setQueryData(['discovery'], (current: { cards: ProfileCard[] } | undefined) =>
          withDiscoveryProfileAt(current, undo.card, undo.index),
        );
      }
      setIndex(undo?.index ?? 0);
      setUndo(null);
    },
  });
  const undoAction = undo ? (
    <div className="discovery-undo">
      <Button
        tone="secondary"
        disabled={undoMutation.isPending}
        onClick={() => undoMutation.mutate()}
      >
        Вернуть прошлую анкету
      </Button>
    </div>
  ) : null;
  if (eligibility.data && !eligibility.data.isComplete)
    return (
      <Screen title="Знакомства">
        <Card>
          <h2>Завершите анкету</h2>
          <p>Остался один шаг, чтобы начать знакомиться.</p>
          <Button
            type="button"
            onClick={() => {
              void navigate(`/onboarding/${eligibility.data?.onboardingStep ?? 'basics'}`);
            }}
          >
            Продолжить
          </Button>
        </Card>
      </Screen>
    );
  if (query.isLoading || (!card && query.isFetching))
    return (
      <Screen title="Знакомства">
        <DiscoverySkeleton />
      </Screen>
    );
  if (query.isError)
    return (
      <Screen title="Знакомства">
        <AsyncState kind="error" description="Не удалось загрузить анкеты." />
        <Button tone="secondary" onClick={() => void query.refetch()}>
          Попробовать ещё раз
        </Button>
      </Screen>
    );
  if (!card)
    return (
      <Screen title={query.data?.canExpand ? 'Поиск завершён' : 'Пока никого нового'}>
        <AsyncState
          kind="empty"
          description={
            query.data?.canExpand
              ? 'По вашим текущим настройкам анкеты закончились'
              : 'Доступных анкет сейчас больше нет. Загляните немного позже.'
          }
        />
        {query.data?.canExpand ? (
          <Button disabled={expandMutation.isPending} onClick={() => expandMutation.mutate()}>
            Расширить поиск
          </Button>
        ) : null}
        {expandMutation.isError ? (
          <p role="alert">Не удалось расширить поиск. Попробуйте ещё раз.</p>
        ) : null}
        <Link className="button button--secondary" to="/search-settings">
          Настроить поиск
        </Link>
        {undoAction}
      </Screen>
    );
  const photos = card.photos ?? [];
  const visiblePhoto = photos[photoIndex] ?? photos[0];
  const interests = profileInterests(card);
  const showPreviousPhoto = () =>
    setPhotoIndex((value) => (value + photos.length - 1) % photos.length);
  const showNextPhoto = () => setPhotoIndex((value) => (value + 1) % photos.length);
  return (
    <Screen title="Знакомства" eyebrow="Анкеты" className="screen--discover">
      <article className="profile-card">
        <div
          className="profile-photo"
          onTouchStart={(event) => {
            discoveryTouchStart.current = event.touches[0]?.clientX ?? null;
          }}
          onTouchEnd={(event) => {
            const start = discoveryTouchStart.current;
            const end = event.changedTouches[0]?.clientX;
            if (
              start !== null &&
              end !== undefined &&
              photos.length > 1 &&
              Math.abs(start - end) > 40
            ) {
              if (start > end) showNextPhoto();
              else showPreviousPhoto();
            }
            discoveryTouchStart.current = null;
          }}
        >
          <PublicPhoto
            key={visiblePhoto?.assetId ?? photoIndex}
            photoId={visiblePhoto?.assetId}
            url={visiblePhoto?.url}
            alt={`Фото ${photoIndex + 1} профиля ${card.displayName}`}
            onRecover={async () => {
              const refreshed = await query.refetch();
              if (refreshed.isError) throw refreshed.error;
            }}
          />
          {card.searchExpanded && <span className="profile-card__expanded">Поиск расширен</span>}
          {photos.length > 1 && (
            <>
              <div
                className="discovery-photo-indicators"
                aria-label={`Фото ${photoIndex + 1} из ${photos.length}`}
              >
                {photos.map((_, value) => (
                  <span key={value} className={value === photoIndex ? 'is-active' : ''} />
                ))}
              </div>
              <button
                className="discovery-photo-zone discovery-photo-zone--previous"
                type="button"
                aria-label="Предыдущее фото"
                onClick={showPreviousPhoto}
              />
              <button
                className="discovery-photo-zone discovery-photo-zone--next"
                type="button"
                aria-label="Следующее фото"
                onClick={showNextPhoto}
              />
            </>
          )}
          <div className="profile-card__overlay">
            <h2>
              {card.displayName}, {card.age}
            </h2>
            <p className="profile-card__location">
              <span aria-hidden="true">⌖</span> {card.city}
            </p>
            {card.bio && <p className="profile-card__bio">{card.bio}</p>}
            {card.typology && (
              <p className="typology-chip">
                {card.typology.emoji} Типаж: {card.typology.title}
              </p>
            )}
            {interests.length > 0 && (
              <div className="profile-card__interests" aria-label="Интересы">
                {interests.map((interest) => (
                  <span key={interest}>{interest}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      </article>
      <div className="discovery-actions">
        <Button
          tone="secondary"
          className="discovery-action discovery-action--pass"
          onClick={() => mutation.mutate({ type: 'pass', profile: card })}
          aria-label="Пропустить"
          disabled={mutation.isPending}
        >
          <DiscoveryActionIcon name="pass" />
          Пропустить
        </Button>
        <Button
          tone="secondary"
          className="discovery-action discovery-action--profile"
          onClick={() =>
            void navigate(`/profile/${card.id}`, {
              state: { source: 'discover', candidateId: card.id },
            })
          }
          aria-label="Открыть анкету"
        >
          <DiscoveryActionIcon name="profile" />
          Анкета
        </Button>
        <Button
          className="discovery-action discovery-action--like"
          onClick={() => {
            mutation.mutate({ type: 'like', profile: card });
          }}
          aria-label="Поставить лайк"
          disabled={mutation.isPending}
        >
          <DiscoveryActionIcon name="like" />
          Нравится
        </Button>
      </div>
      {undoAction}
    </Screen>
  );
}

export function PublicProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { profileId = '' } = useParams();
  const ownProfile = profileId === 'me';
  const queryClient = useQueryClient();
  const context = (location.state as PublicProfileContext | null) ?? {};
  const fromLikes = context.source === 'likes';
  const fromChat = context.source === 'chat';
  const query = useQuery({
    queryKey: ['public-profile', profileId, context.source],
    queryFn: async () => {
      if (ownProfile) {
        const [profile, ownerPhotos] = await Promise.all([
          api<{ public?: ProfileCard }>('/me/profile'),
          api<OwnerPhoto[]>('/me/photos'),
        ]);
        if (!profile.public) throw new Error('Own profile is unavailable');
        return {
          ...profile.public,
          photos: ownerPhotos
            .filter((photo) => photo.status === 'READY')
            .sort((left, right) => left.position - right.position)
            .map((photo) => ({
              assetId: photo.assetId,
              ...(photo.previewUrl?.url ? { url: photo.previewUrl.url } : {}),
            })),
        };
      }
      return hydratePublicPhotos(
        await api<ProfileCard>(
          `/profiles/${profileId}${fromLikes ? '?source=likes' : fromChat ? '?source=chat' : ''}`,
        ),
      );
    },
    retry: false,
  });
  const [photoSelection, setPhotoSelection] = useState<GallerySelection>({
    photoId: null,
    index: 0,
  });
  const touchStart = useRef<number | null>(null);
  const action = useMutation({
    mutationFn: async (type: 'pass' | 'like') => {
      if (fromLikes) {
        if (!context.reactionId) throw new Error('Incoming like is unavailable');
        return api<{ match?: { conversationId?: string } | null }>(
          `/likes/${context.reactionId}/${type === 'pass' ? 'pass' : 'accept'}`,
          { method: 'POST', body: '{}' },
        );
      }
      return api<{ match?: { conversationId?: string } | null }>(`/profiles/${profileId}/${type}`, {
        method: 'POST',
        body: JSON.stringify({ source: context.source ?? 'discover' }),
      });
    },
    onSuccess: (result, type) => {
      if (type === 'like' && result.match?.conversationId && query.data) {
        clearMatchedProfileCaches(queryClient, profileId);
        void navigate('/match', {
          replace: true,
          state: {
            profile: query.data,
            conversationId: result.match.conversationId,
            returnTo: fromLikes ? '/likes' : '/discover',
            ...(context.likesScrollY === undefined ? {} : { likesScrollY: context.likesScrollY }),
          } satisfies MatchRouteState,
        });
        return;
      }
      if (fromLikes) {
        queryClient.setQueryData<{ items: IncomingLike[] }>(['likes'], (current) =>
          current
            ? { items: current.items.filter((item) => item.reactionId !== context.reactionId) }
            : current,
        );
        void queryClient.invalidateQueries({ queryKey: ['likes'] });
      } else {
        queryClient.setQueryData<{ cards: ProfileCard[] }>(['discovery'], (current) =>
          withoutDiscoveryProfile(current, profileId),
        );
        void queryClient.invalidateQueries({ queryKey: ['discovery'] });
      }
      void navigate(fromLikes ? '/likes' : '/discover', {
        replace: true,
        state: fromLikes
          ? { scrollY: context.likesScrollY }
          : { candidateId: context.candidateId ?? profileId },
      });
    },
  });
  const back = () =>
    void navigate(
      ownProfile
        ? '/profile'
        : fromLikes
          ? '/likes'
          : fromChat && context.conversationId
            ? `/chat/${context.conversationId}`
            : '/discover',
      {
        replace: true,
        state: fromLikes
          ? { scrollY: context.likesScrollY }
          : { candidateId: context.candidateId ?? profileId },
      },
    );
  if (query.isLoading)
    return (
      <Screen title="Анкета">
        <PublicProfileSkeleton />
      </Screen>
    );
  if (query.isError || !query.data)
    return (
      <Screen title="Анкета">
        <AsyncState kind="error" description="Анкета больше недоступна." />
        <Button tone="secondary" onClick={() => void query.refetch()}>
          Повторить
        </Button>
        <Button tone="ghost" onClick={back}>
          Назад
        </Button>
      </Screen>
    );
  const profile = query.data;
  const photos = profile.photos ?? [];
  const resolvedPhoto = resolveGallerySelection(photos, photoSelection);
  const photoIndex = resolvedPhoto.index;
  const photo = resolvedPhoto.photo;
  const selectPhoto = (nextIndex: number) => {
    if (photos.length === 0) return;
    const index = (nextIndex + photos.length) % photos.length;
    setPhotoSelection({ photoId: photos[index]?.assetId ?? null, index });
  };
  const interests = profileInterests(profile, 8);
  const facts: Array<[string, string | undefined]> = [
    ['Работа', profile.details?.work],
    ['Учёба', profile.details?.education],
    ['Домашние животные', profile.details?.pets?.join(', ')],
    ['Смотрит', profile.details?.favoriteFilm],
    ['Слушает', profile.details?.favoriteMusic],
  ];
  return (
    <Screen
      title="Анкета"
      className="screen--public-profile"
      action={
        <Button tone="ghost" onClick={back}>
          Назад
        </Button>
      }
    >
      <article className="public-profile">
        <div className="public-profile__gallery" aria-label="Фотографии анкеты">
          <div
            onTouchStart={(event) => {
              touchStart.current = event.touches[0]?.clientX ?? null;
            }}
            onTouchEnd={(event) => {
              const start = touchStart.current;
              const end = event.changedTouches[0]?.clientX;
              if (
                start !== null &&
                end !== undefined &&
                photos.length > 1 &&
                Math.abs(start - end) > 40
              )
                selectPhoto(photoIndex + (start > end ? 1 : -1));
              touchStart.current = null;
            }}
          >
            <PublicPhoto
              key={photo?.assetId ?? photoIndex}
              photoId={photo?.assetId}
              url={photo?.url}
              alt={`Фото ${photoIndex + 1} профиля ${profile.displayName}`}
              onRecover={async () => {
                const refreshed = await query.refetch();
                if (refreshed.isError) throw refreshed.error;
              }}
            />
          </div>
          {photos.length > 1 && (
            <>
              <button
                type="button"
                className="gallery-control gallery-control--previous"
                aria-label="Предыдущее фото"
                onClick={() => selectPhoto(photoIndex - 1)}
              >
                ‹
              </button>
              <button
                type="button"
                className="gallery-control gallery-control--next"
                aria-label="Следующее фото"
                onClick={() => selectPhoto(photoIndex + 1)}
              >
                ›
              </button>
              <div
                className="gallery-indicators"
                aria-label={`Фото ${photoIndex + 1} из ${photos.length}`}
              >
                {photos.map((_, value) => (
                  <span key={value} className={value === photoIndex ? 'is-active' : ''} />
                ))}
              </div>
            </>
          )}
        </div>
        <div className="public-profile__body">
          <div className="public-profile__identity">
            <div>
              <h2>
                {profile.displayName}, {profile.age}
              </h2>
              <p className="meta">⌖ {[profile.city, profile.country].filter(Boolean).join(', ')}</p>
            </div>
            {profile.goal && (
              <p className="public-profile__goal">{profileGoalLabel(profile.goal)}</p>
            )}
          </div>
          {profile.bio && (
            <section className="public-profile__section public-profile__section--about">
              <h3>О себе</h3>
              <p>{profile.bio}</p>
            </section>
          )}
          {interests.length > 0 && (
            <section className="public-profile__section">
              <h3>Интересы</h3>
              <div className="public-profile__chips">
                {interests.map((interest) => (
                  <span key={interest}>{interest}</span>
                ))}
              </div>
            </section>
          )}
          {facts.some(([, value]) => value?.trim()) && (
            <section className="public-profile__section">
              <h3>Ближе познакомиться</h3>
              <div className="public-profile__facts">
                {facts
                  .filter(([, value]) => value?.trim())
                  .map(([title, value]) => (
                    <div key={title}>
                      <span>{title}</span>
                      <strong>{value}</strong>
                    </div>
                  ))}
              </div>
            </section>
          )}
        </div>
      </article>
      {ownProfile && (
        <div className="public-profile__actions">
          <Link className="button button--secondary" to="/profile/edit">
            Редактировать
          </Link>
          <Link className="button button--primary" to="/profile/photos">
            Фотографии
          </Link>
        </div>
      )}
      {fromLikes && context.mutual && context.conversationId ? (
        <div className="public-profile__actions">
          <Link className="button button--primary" to={`/chat/${context.conversationId}`}>
            Открыть чат
          </Link>
        </div>
      ) : (
        (context.source === 'discover' || fromLikes) && (
          <div className="public-profile__actions">
            <Button
              tone="secondary"
              disabled={action.isPending}
              onClick={() => action.mutate('pass')}
            >
              Пропустить
            </Button>
            <Button disabled={action.isPending} onClick={() => action.mutate('like')}>
              {fromLikes ? '❤️ Взаимно' : 'Нравится'}
            </Button>
            {action.isError && (
              <p className="form-error">Не удалось выполнить действие. Повторите попытку.</p>
            )}
          </div>
        )
      )}
    </Screen>
  );
}

function DiscoverySkeleton() {
  return (
    <section className="profile-card profile-card--skeleton" aria-label="Загружаем анкеты">
      <div className="profile-photo" />
      <div className="profile-card__body">
        <span />
        <span />
      </div>
    </section>
  );
}

function PublicProfileSkeleton() {
  return (
    <section className="public-profile public-profile--skeleton" aria-label="Загружаем анкету">
      <div className="public-profile__gallery" />
      <div className="public-profile__body">
        <span />
        <span />
        <span />
      </div>
    </section>
  );
}

export function LikesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['likes'],
    queryFn: async () => {
      const result = await api<{ items: IncomingLike[]; observedAt: string }>('/likes/incoming');
      return {
        observedAt: result.observedAt,
        items: await Promise.all(
          result.items.map(async (item) => ({
            ...item,
            profile: await hydratePublicPhotos(item.profile),
          })),
        ),
      };
    },
    refetchOnMount: 'always',
  });
  useEffect(() => {
    if (!query.data?.observedAt) return;
    void api('/likes/seen', {
      method: 'POST',
      body: JSON.stringify({ seenThrough: query.data.observedAt }),
    })
      .then(() => queryClient.invalidateQueries({ queryKey: ['likes-unread'] }))
      .catch(() => undefined);
  }, [query.data?.observedAt, queryClient]);
  const scrollY = (location.state as { scrollY?: number } | null)?.scrollY;
  useEffect(() => {
    if (scrollY === undefined || !query.data) return;
    const frame = window.requestAnimationFrame(() => window.scrollTo({ top: scrollY }));
    return () => window.cancelAnimationFrame(frame);
  }, [query.data, scrollY]);
  if (query.isLoading)
    return (
      <Screen title="Вас лайкнули">
        <LikesSkeleton />
      </Screen>
    );
  if (query.isError)
    return (
      <Screen title="Вас лайкнули">
        <AsyncState kind="error" description="Не удалось загрузить лайки." />
        <Button tone="secondary" onClick={() => void query.refetch()}>
          Повторить
        </Button>
      </Screen>
    );
  if (!query.data?.items.length)
    return (
      <Screen title="Лайки" className="screen--likes screen--likes-empty">
        <section className="likes-empty" role="status">
          <span className="likes-empty__icon" aria-hidden="true">
            ♡
          </span>
          <h2>Пока тихо</h2>
          <p>{incomingLikesEmptyDescription} А пока можно посмотреть ещё анкеты.</p>
          <Link className="button button--primary" to="/discover">
            Смотреть анкеты
          </Link>
        </section>
      </Screen>
    );
  return (
    <Screen title="Лайки" className="screen--likes">
      <p className="likes-intro">Здесь люди, которым вы уже понравились.</p>
      <div className="likes-grid">
        {query.data.items.map((item) => (
          <LikeRow
            key={item.reactionId}
            item={item}
            onOpen={() =>
              void navigate(`/profile/${item.profile.id}`, {
                state: {
                  source: 'likes',
                  reactionId: item.reactionId,
                  likesScrollY: window.scrollY,
                  ...(item.conversationId ? { conversationId: item.conversationId } : {}),
                  mutual: item.mutual,
                } satisfies PublicProfileContext,
              })
            }
          />
        ))}
      </div>
    </Screen>
  );
}

function LikesSkeleton() {
  return (
    <div className="likes-grid" aria-label="Загружаем лайки">
      <div className="like-card like-card--skeleton" />
      <div className="like-card like-card--skeleton" />
    </div>
  );
}

function LikeRow({ item, onOpen }: { item: IncomingLike; onOpen: () => void }) {
  const interests = profileInterests(item.profile, 3);
  return (
    <article className={`like-card${item.mutual ? ' like-card--mutual' : ''}`}>
      <button className="like-card__profile" type="button" onClick={onOpen}>
        <span className="like-card__photo">
          <PublicPhoto
            url={item.profile.photos?.[0]?.url}
            alt={`Фото профиля ${item.profile.displayName}`}
          />
          <span className="like-card__badge">
            <span aria-hidden="true">♡</span> {item.mutual ? 'Взаимно' : 'Вы нравитесь'}
          </span>
        </span>
        <span className="like-card__body">
          <strong>
            {item.profile.displayName}, {item.profile.age}
          </strong>
          <small>⌖ {item.profile.city || 'Город не указан'}</small>
          {interests.length > 0 && (
            <span className="like-card__interests">
              {interests.map((interest) => (
                <span key={interest}>{interest}</span>
              ))}
            </span>
          )}
          {item.intro && <em>{item.intro}</em>}
        </span>
      </button>
      {item.mutual && item.conversationId && (
        <div className="like-card__actions">
          <Button tone="secondary" onClick={onOpen}>
            Открыть анкету
          </Button>
          <Link className="button button--primary" to={`/chat/${item.conversationId}`}>
            Открыть чат
          </Link>
        </div>
      )}
    </article>
  );
}

export function MatchPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as MatchRouteState | null) ?? {};
  const ownPhotos = useQuery({
    queryKey: ['match-own-photos'],
    queryFn: () => api<{ isPrimary: boolean; previewUrl?: OwnerPhotoPreview }[]>('/me/photos'),
    enabled: Boolean(state.profile),
  });
  const back = () =>
    void navigate(state.returnTo ?? '/likes', {
      replace: true,
      state: { scrollY: state.likesScrollY },
    });
  if (!state.profile || !state.conversationId)
    return (
      <Screen title="Это взаимно">
        <AsyncState kind="error" description="Этот экран больше недоступен." />
        <Button tone="secondary" onClick={back}>
          К лайкам
        </Button>
      </Screen>
    );
  const ownPhoto = ownerPhotoPreviewSrc(
    ownPhotos.data?.find((photo) => photo.isPrimary)?.previewUrl,
    localAcceptanceEnabled(),
  );
  return (
    <Screen title="Это взаимно" className="screen--match">
      <section className="match-card">
        <div className="match-card__photos">
          <div>
            <PublicPhoto url={ownPhoto ?? undefined} alt="Ваше фото" />
          </div>
          <span aria-hidden="true">❤️</span>
          <div>
            <PublicPhoto
              url={state.profile.photos?.[0]?.url}
              alt={`Фото профиля ${state.profile.displayName}`}
            />
          </div>
        </div>
        <h2>Это взаимно ❤️</h2>
        <p>Вы понравились друг другу</p>
      </section>
      <Button onClick={() => void navigate(`/chat/${state.conversationId}`)}>
        Написать сообщение
      </Button>
      <Button tone="secondary" onClick={back}>
        Продолжить знакомства
      </Button>
    </Screen>
  );
}

export function ChatsPage() {
  const query = useQuery({
    queryKey: ['conversations'],
    queryFn: () =>
      api<{
        items: {
          id: string;
          displayName: string;
          preview: string;
          unreadCount: number;
          lastMessageAt?: string;
          avatarAssetId?: string | null;
        }[];
      }>('/conversations'),
  });
  if (query.isLoading)
    return (
      <Screen title="Чаты">
        <AsyncState kind="loading" />
      </Screen>
    );
  if (query.isError)
    return (
      <Screen title="Чаты">
        <AsyncState kind="error" />
        <Button tone="secondary" onClick={() => void query.refetch()}>
          Повторить
        </Button>
      </Screen>
    );
  if (!query.data?.items.length)
    return (
      <Screen title="Чаты" className="screen--chats screen--chats-empty">
        <section className="chats-empty" role="status">
          <span className="chats-empty__icon" aria-hidden="true">
            <ChatSurfaceIcon name="chat" />
          </span>
          <h2>Пока нет переписок</h2>
          <p>Когда вы начнёте разговор, он появится здесь.</p>
          <Link className="button button--primary" to="/discover">
            Смотреть знакомства
          </Link>
        </section>
      </Screen>
    );
  return (
    <Screen title="Чаты" className="screen--chats">
      <div className="chat-list">
        {query.data.items.map((chat) =>
          isUnavailableConversation(chat.displayName) ? (
            <article className="chat-row chat-row--unavailable" key={chat.id}>
              <Avatar label="·" decorative />
              <span>
                <strong>{deletedConversationName}</strong>
                <small>Разговор больше недоступен</small>
              </span>
            </article>
          ) : (
            <Link to={`/chat/${chat.id}`} className="chat-row" key={chat.id}>
              <Avatar label={chat.displayName.slice(0, 1)} assetId={chat.avatarAssetId} />
              <span>
                <strong>{chat.displayName}</strong>
                <small>{chat.preview}</small>
              </span>
              <span className="chat-row__meta">
                {chatItemTime(chat.lastMessageAt) && (
                  <time>{chatItemTime(chat.lastMessageAt)}</time>
                )}
                {chat.unreadCount > 0 && <b>{chat.unreadCount}</b>}
              </span>
            </Link>
          ),
        )}
      </div>
    </Screen>
  );
}

function ChatSurfaceIcon({
  name,
}: {
  name: 'chat' | 'reply' | 'interpret' | 'topic' | 'game' | 'spark' | 'send';
}) {
  return (
    <svg className="chat-surface-icon" viewBox="0 0 24 24" aria-hidden="true">
      {name === 'chat' && (
        <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z" />
      )}
      {name === 'reply' && (
        <>
          <path d="m9 8-5 4 5 4" />
          <path d="M5 12h8a6 6 0 0 1 6 6" />
        </>
      )}
      {name === 'interpret' && (
        <>
          <path d="M4 5h9M8.5 3v2c0 5-2 8-5 10" />
          <path d="M6 10c1.5 2 3.2 3.5 5 4.5M14 19l3-8 3 8m-5-3h4" />
        </>
      )}
      {name === 'topic' && (
        <>
          <path d="M21 15a4 4 0 0 1-4 4H9l-5 3v-5a7 7 0 1 1 17-2Z" />
          <path d="M8 10h8M8 14h5" />
        </>
      )}
      {name === 'game' && (
        <>
          <path d="M8 8h8a5 5 0 0 1 4.7 6.7l-.8 2.3a2 2 0 0 1-3.5.6L14.5 15h-5l-1.9 2.6A2 2 0 0 1 4.1 17l-.8-2.3A5 5 0 0 1 8 8Z" />
          <path d="M8 11v4m-2-2h4m6-1h.01m2 2h.01" />
        </>
      )}
      {(name === 'spark' || name === 'send') &&
        (name === 'spark' ? (
          <>
            <path d="m12 3 1.4 4.6L18 9l-4.6 1.4L12 15l-1.4-4.6L6 9l4.6-1.4L12 3Z" />
            <path d="m18.5 15 .7 2.3 2.3.7-2.3.7-.7 2.3-.7-2.3-2.3-.7 2.3-.7.7-2.3Z" />
          </>
        ) : (
          <path d="m4 4 17 8-17 8 3-8-3-8Zm3 8h14" />
        ))}
    </svg>
  );
}

export function ChatPage() {
  const { conversationId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const timelineKey = ['conversation-items', conversationId] as const;
  const conversation = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () =>
      api<{
        counterpart: { id: string; displayName: string; avatarAssetId?: string | null };
        muted: boolean;
        counterpartReadSeq: number;
        retentionNotice?: RetentionNotice | null;
      }>(`/conversations/${conversationId}`),
  });
  const query = useQuery<ChatTimeline>({
    queryKey: timelineKey,
    queryFn: () =>
      recoverChatTimeline(queryClient.getQueryData<ChatTimeline>(timelineKey), (afterSeq) =>
        api<ChatTimeline>(
          `/conversations/${conversationId}/items?limit=50${afterSeq === undefined ? '' : `&afterSeq=${afterSeq}`}`,
        ),
      ),
  });
  const retryChat = useCallback(() => {
    void conversation.refetch();
    void query.refetch();
  }, [conversation.refetch, query.refetch]);
  const chatErrors = [query.error, conversation.error];
  const preserveRateLimitedChat = preserveChatOnRateLimit(
    query.data,
    conversation.data,
    chatErrors,
  );
  const rateLimitError = chatErrors.find(isRequestRateLimited);
  const [text, setText] = useState(() => getChatState(conversationId).draft);
  const [aiOpen, setAiOpen] = useState(false);
  const [gamesOpen, setGamesOpen] = useState(false);
  const [openedGameId, setOpenedGameId] = useState<string>();
  const [dismissedGameId, setDismissedGameId] = useState<string>();
  const currentGame = useQuery({
    queryKey: ['conversation-game', conversationId],
    queryFn: () => api<{ game: ChatGame | null }>(`/conversations/${conversationId}/games/current`),
  });
  const [aiTask, setAiTask] = useState<ChatAiTask>('reply');
  const [aiTone, setAiTone] = useState<ChatAiTone>('natural');
  const [aiStep, setAiStep] = useState<'root' | 'reply-style' | 'request' | 'result'>('root');
  const [aiResult, setAiResult] = useState<{
    conversationId: string;
    result: ChatAiResult;
    disclaimer: string;
    fallback: boolean;
  }>();
  const [aiError, setAiError] = useState('');
  const [aiExclusions, setAiExclusions] = useState<string[]>([]);
  const [safetyView, setSafetyView] = useState<
    'closed' | 'menu' | 'report' | 'block' | 'unmatch' | 'mute'
  >('closed');
  const [reportCategory, setReportCategory] = useState<ReportCategory>('harassment');
  const [reportDetails, setReportDetails] = useState('');
  const [reportAlsoBlock, setReportAlsoBlock] = useState(false);
  const [unmatchReason, setUnmatchReason] = useState('');
  const [safetyError, setSafetyError] = useState('');
  const [safetyReceipt, setSafetyReceipt] = useState('');
  const [sendError, setSendError] = useState('');
  const [retryMessage, setRetryMessage] = useState<{ clientMessageId: string; text: string }>();
  const [isAtLatest, setIsAtLatest] = useState(true);
  const [unseenMessages, setUnseenMessages] = useState(0);
  const [timelinePositioned, setTimelinePositioned] = useState(false);
  const [visibilityState, setVisibilityState] = useState<DocumentVisibilityState>(
    () => document.visibilityState,
  );
  const [historyExhausted, setHistoryExhausted] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const timelineRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const itemCountRef = useRef(0);
  const positionedConversationRef = useRef<string | undefined>(undefined);
  const pendingPrependRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(null);
  const aiConversationRef = useRef(conversationId);
  const send = useMutation({
    mutationFn: (input: { clientMessageId: string; text: string }) =>
      api<ChatTimelineItem>(`/conversations/${conversationId}/messages`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onMutate: async (input) => {
      setSendError('');
      await queryClient.cancelQueries({ queryKey: timelineKey });
      const previous = queryClient.getQueryData<ChatTimeline>(timelineKey);
      queryClient.setQueryData<ChatTimeline>(
        timelineKey,
        mergeChatTimeline(previous, {
          items: [
            {
              id: `optimistic:${input.clientMessageId}`,
              seq: (lastConfirmedSeq(previous) ?? 0) + 1,
              kind: 'TEXT',
              own: true,
              text: input.text,
              optimistic: true,
            },
          ],
        }),
      );
      return { previous };
    },
    onError: (reason, input) => {
      queryClient.setQueryData<ChatTimeline>(timelineKey, (current) =>
        current
          ? {
              ...current,
              items: current.items.map((item) =>
                item.id === `optimistic:${input.clientMessageId}`
                  ? { ...item, failed: true }
                  : item,
              ),
            }
          : current,
      );
      setRetryMessage(input);
      setSendError(
        reason instanceof ApiClientError ? reason.message : 'Не удалось отправить сообщение',
      );
    },
    onSuccess: async (message, input) => {
      const current = queryClient.getQueryData<ChatTimeline>(timelineKey);
      const withoutOptimistic = current
        ? {
            ...current,
            items: current.items.filter(
              (item) => item.id !== `optimistic:${input.clientMessageId}`,
            ),
          }
        : undefined;
      queryClient.setQueryData(
        timelineKey,
        mergeChatTimeline(withoutOptimistic, { items: [message] }),
      );
      // Поле уже очищено при отправке: здесь его трогать нельзя, иначе сотрётся
      // следующее сообщение, которое пользователь начал печатать за время доставки.
      setRetryMessage(undefined);
      void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      await queryClient.invalidateQueries({ queryKey: timelineKey });
    },
  });
  const latestSeq = lastConfirmedSeq(query.data);
  const firstSeq = query.data?.cursors?.firstSeq;
  const hasOlder = !historyExhausted && firstSeq !== undefined && firstSeq > 1;
  const loadOlder = async () => {
    const timeline = timelineRef.current;
    if (!timeline || !firstSeq || isLoadingOlder) return;
    setIsLoadingOlder(true);
    setHistoryError('');
    pendingPrependRef.current = {
      scrollHeight: timeline.scrollHeight,
      scrollTop: timeline.scrollTop,
    };
    try {
      const page = await api<ChatTimeline>(
        `/conversations/${conversationId}/items?limit=50&beforeSeq=${firstSeq}`,
      );
      if (page.items.length === 0) {
        pendingPrependRef.current = null;
        setHistoryExhausted(true);
        return;
      }
      queryClient.setQueryData<ChatTimeline>(timelineKey, (current) =>
        mergeChatTimeline(current, page),
      );
      setHistoryExhausted((page.cursors?.firstSeq ?? firstSeq) <= 1);
    } catch {
      pendingPrependRef.current = null;
      setHistoryError('Не удалось загрузить предыдущие сообщения');
    } finally {
      setIsLoadingOlder(false);
    }
  };
  useEffect(() => {
    setHistoryExhausted(false);
    setHistoryError('');
    setTimelinePositioned(false);
    pendingPrependRef.current = null;
    itemCountRef.current = 0;
  }, [conversationId]);
  useEffect(() => {
    const timeline = timelineRef.current;
    if (!timeline || !query.data) return;
    const itemCount = query.data.items.length;
    const previousCount = itemCountRef.current;
    itemCountRef.current = itemCount;
    const pendingPrepend = pendingPrependRef.current;
    if (pendingPrepend) {
      pendingPrependRef.current = null;
      const frame = requestAnimationFrame(() => restoreChatAfterPrepend(timeline, pendingPrepend));
      setIsAtLatest(false);
      return () => cancelAnimationFrame(frame);
    }
    if (positionedConversationRef.current !== conversationId) {
      positionedConversationRef.current = conversationId;
      const savedScrollTop = getChatState(conversationId).scrollTop;
      const frame = requestAnimationFrame(() => {
        if (savedScrollTop > 0) {
          timeline.scrollTop = savedScrollTop;
          setIsAtLatest(isChatNearLatest(timeline));
        } else {
          scrollChatToLatest(timeline);
          setIsAtLatest(true);
        }
        setTimelinePositioned(true);
      });
      return () => cancelAnimationFrame(frame);
    }
    if (isAtLatest) {
      const frame = requestAnimationFrame(() => scrollChatToLatest(timeline));
      return () => cancelAnimationFrame(frame);
    }
    if (itemCount > previousCount) setUnseenMessages((count) => count + itemCount - previousCount);
    return undefined;
  }, [conversationId, isAtLatest, query.data]);
  useEffect(() => {
    const viewport = window.visualViewport;
    const updateHeight = () => {
      document.documentElement.style.setProperty(
        '--chat-viewport-height',
        `${Math.round(viewport?.height ?? window.innerHeight)}px`,
      );
      if (isAtLatest && timelineRef.current)
        requestAnimationFrame(() => timelineRef.current && scrollChatToLatest(timelineRef.current));
    };
    updateHeight();
    viewport?.addEventListener('resize', updateHeight);
    window.addEventListener('resize', updateHeight);
    return () => {
      viewport?.removeEventListener('resize', updateHeight);
      window.removeEventListener('resize', updateHeight);
      document.documentElement.style.removeProperty('--chat-viewport-height');
    };
  }, [isAtLatest]);
  useEffect(() => {
    const updateVisibility = () => setVisibilityState(document.visibilityState);
    document.addEventListener('visibilitychange', updateVisibility);
    return () => document.removeEventListener('visibilitychange', updateVisibility);
  }, []);
  useEffect(() => {
    if (
      !shouldAcknowledgeChatRead({
        latestSeq,
        timelinePositioned:
          timelinePositioned && positionedConversationRef.current === conversationId,
        isAtLatest,
        visibilityState,
      })
    )
      return;
    // A read cursor also advances delivered state atomically on the server. Sending it directly
    // avoids making the authoritative read acknowledgement depend on a separate precursor call.
    void api(`/conversations/${conversationId}/read-cursor`, {
      method: 'PUT',
      body: JSON.stringify({ readSeq: latestSeq }),
    })
      .then(() => {
        // The server read cursor is authoritative. Reconcile both Chats consumers immediately,
        // even when the chat unmounts before this request completes, then refresh from the server.
        queryClient.setQueryData(['conversations'], (current) =>
          clearConversationUnread(current as never, conversationId),
        );
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      })
      .catch(() => undefined);
    return undefined;
  }, [conversationId, isAtLatest, latestSeq, queryClient, timelinePositioned, visibilityState]);
  const aiStatus = useQuery({
    queryKey: ['ai-status'],
    queryFn: () =>
      api<{ enabled: boolean; interpretEnabled: boolean; consent: { assistantUse: boolean } }>(
        '/ai/status',
      ),
    enabled: aiOpen,
  });
  const latestIncomingMessage = [...(query.data?.items ?? [])]
    .reverse()
    .find((item) => !item.own && (item.kind === 'TEXT' || item.kind === 'INTRO') && item.text);
  const ai = useMutation({
    mutationFn: () => {
      const requestConversationId = conversationId;
      const request = chatAiRequest(aiTask, conversationId, {
        tone: aiTone,
        ...(latestIncomingMessage ? { focusMessageId: latestIncomingMessage.id } : {}),
        ...(aiExclusions.length ? { exclude: aiExclusions } : {}),
      });
      if (!request) throw new Error('Выберите входящее сообщение для разбора');
      return api<{
        requestId: string;
        result: ChatAiResult;
        disclaimer: string;
        fallback: boolean;
      }>(request.path, {
        method: 'POST',
        body: JSON.stringify(request.body),
      }).then((response) => ({
        ...response,
        conversationId: requestConversationId,
      }));
    },
    onSuccess: (response) => {
      // A late reply from Chat A must never surface after navigation to Chat B.
      if (response.conversationId !== aiConversationRef.current) return;
      setAiError('');
      setAiResult(response);
      setAiStep('result');
    },
    onError: (reason: unknown, _variables, context) => {
      if (context?.conversationId !== aiConversationRef.current) return;
      setAiError(
        reason instanceof ApiClientError &&
          [
            'AI_TEMPORARILY_UNAVAILABLE',
            'AI_QUOTA_EXHAUSTED',
            'INVALID_ACCOUNT_STATE',
            'RATE_LIMITED',
          ].includes(reason.code)
          ? reason.message
          : 'Не удалось связаться с AI. Повторите запрос чуть позже. Чат продолжает работать.',
      );
    },
    onMutate: () => {
      setAiError('');
      return { conversationId };
    },
  });
  const report = useMutation({
    mutationFn: () => {
      const target = conversation.data?.counterpart;
      if (!target) throw new Error('Собеседник не найден');
      return api('/reports', {
        method: 'POST',
        body: JSON.stringify(
          buildReportRequest({
            targetUserId: target.id,
            conversationId,
            category: reportCategory,
            details: reportDetails,
            block: reportAlsoBlock,
          }),
        ),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['conversations'] });
      if (reportAlsoBlock) {
        void navigate('/chats', { replace: true });
        return;
      }
      setSafetyReceipt('Жалоба отправлена. Мы рассмотрим её и не раскроем ваше имя.');
      setSafetyView('closed');
      setReportDetails('');
    },
    onError: (reason: unknown) =>
      setSafetyError(
        reason instanceof ApiClientError ? reason.message : 'Не удалось отправить жалобу',
      ),
  });
  const block = useMutation({
    mutationFn: () => {
      const target = conversation.data?.counterpart;
      if (!target) throw new Error('Собеседник не найден');
      return api(`/users/${target.id}/block`, {
        method: 'POST',
        body: JSON.stringify({ source: 'CHAT' }),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['conversations'] });
      void navigate('/chats', { replace: true });
    },
    onError: (reason: unknown) =>
      setSafetyError(
        reason instanceof ApiClientError ? reason.message : 'Не удалось заблокировать',
      ),
  });
  const unmatch = useMutation({
    mutationFn: () =>
      api(`/conversations/${conversationId}/unmatch`, {
        method: 'POST',
        body: JSON.stringify(buildUnmatchRequest(unmatchReason)),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['conversations'] });
      void navigate('/chats', { replace: true });
    },
    onError: (reason: unknown) =>
      setSafetyError(
        reason instanceof ApiClientError ? reason.message : 'Не удалось завершить знакомство',
      ),
  });
  const mute = useMutation({
    mutationFn: (muted: boolean) =>
      api(`/conversations/${conversationId}/mute`, {
        method: 'PUT',
        body: JSON.stringify({ muted }),
      }),
    onSuccess: async (_result, muted) => {
      await queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      setSafetyReceipt(
        muted ? 'Уведомления этой беседы отключены.' : 'Уведомления этой беседы включены.',
      );
      setSafetyView('closed');
    },
    onError: (reason: unknown) =>
      setSafetyError(
        reason instanceof ApiClientError ? reason.message : 'Не удалось изменить уведомления',
      ),
  });
  const openSafetyView = (view: 'menu' | 'report' | 'block' | 'unmatch' | 'mute') => {
    setSafetyError('');
    setSafetyReceipt('');
    setSafetyView(view);
  };
  useEffect(() => {
    aiConversationRef.current = conversationId;
    setAiOpen(false);
    setAiStep('root');
    setAiResult(undefined);
    setAiError('');
    setAiExclusions([]);
    ai.reset();
  }, [conversationId]);
  useEffect(() => {
    const canonical = currentGame.data?.game;
    if (!shouldAutoOpenCurrentGame(canonical, openedGameId, dismissedGameId)) return;
    setGamesOpen(false);
    setAiOpen(false);
    setOpenedGameId(canonical!.id);
  }, [currentGame.data?.game, dismissedGameId, openedGameId]);
  useEffect(() => {
    const closeModal = (event: Event) => {
      if (safetyView !== 'closed') {
        event.preventDefault();
        setSafetyView('closed');
      } else if (aiOpen) {
        event.preventDefault();
        if (aiStep !== 'root') {
          setAiResult(undefined);
          setAiStep('root');
        } else {
          setAiOpen(false);
        }
        setAiError('');
      } else if (gamesOpen) {
        event.preventDefault();
        setGamesOpen(false);
        setAiOpen(true);
        setAiStep('root');
      } else if (openedGameId) {
        event.preventDefault();
        setOpenedGameId(undefined);
      }
    };
    window.addEventListener('ponchik:back', closeModal);
    return () => window.removeEventListener('ponchik:back', closeModal);
  }, [aiOpen, aiStep, aiTask, gamesOpen, openedGameId, safetyView]);
  return (
    <Screen
      className="screen--chat"
      title={conversation.data?.counterpart.displayName ?? 'Разговор'}
      header={
        <header className="chat-header">
          <IconButton aria-label="Назад к чатам" onClick={() => void navigate('/chats')}>
            ‹
          </IconButton>
          <button
            type="button"
            className="chat-header__profile"
            onClick={() =>
              void navigate(`/profile/${conversation.data?.counterpart.id ?? ''}`, {
                state: { source: 'chat', conversationId },
              })
            }
            disabled={!conversation.data?.counterpart.id}
          >
            <Avatar
              label={(conversation.data?.counterpart.displayName ?? 'С').slice(0, 1)}
              assetId={conversation.data?.counterpart.avatarAssetId}
              decorative
            />
            <span>
              <strong>{conversation.data?.counterpart.displayName ?? 'Разговор'}</strong>
              <small>Ваш мэтч · открыть анкету</small>
            </span>
          </button>
          <IconButton
            aria-label="Действия с разговором"
            aria-expanded={safetyView !== 'closed'}
            onClick={() => {
              if (safetyView === 'closed') openSafetyView('menu');
              else setSafetyView('closed');
            }}
          >
            •••
          </IconButton>
        </header>
      }
    >
      {safetyReceipt && (
        <p className="safety-receipt" role="status">
          {safetyReceipt}
        </p>
      )}
      <ChatRetentionNotice
        notice={conversation.data?.retentionNotice}
        latestActivityAt={query.data?.items.filter((item) => !item.optimistic).at(-1)?.createdAt}
      />
      {safetyView !== 'closed' && (
        <ModalSheet label="Безопасность и знакомство" onClose={() => setSafetyView('closed')}>
          <section className="safety-sheet" aria-labelledby="safety-title">
            <div className="safety-sheet__header">
              <div>
                <strong id="safety-title">
                  {safetyView === 'menu'
                    ? 'Безопасность и знакомство'
                    : safetyView === 'report'
                      ? 'Пожаловаться'
                      : safetyView === 'block'
                        ? 'Заблокировать пользователя'
                        : safetyView === 'unmatch'
                          ? 'Завершить знакомство'
                          : 'Уведомления беседы'}
                </strong>
                {safetyView === 'menu' && <p>Выберите одно действие.</p>}
              </div>
              <IconButton aria-label="Закрыть" onClick={() => setSafetyView('closed')}>
                ×
              </IconButton>
            </div>
            {safetyView === 'menu' && (
              <div className="safety-sheet__actions">
                <button
                  type="button"
                  onClick={() =>
                    void navigate(`/profile/${conversation.data?.counterpart.id ?? ''}`, {
                      state: { source: 'chat', conversationId },
                    })
                  }
                  disabled={!conversation.data?.counterpart.id}
                >
                  <strong>Посмотреть профиль</strong>
                </button>
                <button type="button" onClick={() => openSafetyView('mute')}>
                  <strong>
                    {conversation.data?.muted ? 'Включить уведомления' : 'Выключить уведомления'}
                  </strong>
                </button>
                <button
                  className="safety-action safety-action--first"
                  type="button"
                  onClick={() => openSafetyView('unmatch')}
                >
                  <strong>Удалить пару</strong>
                </button>
                <button
                  className="safety-action"
                  type="button"
                  onClick={() => openSafetyView('block')}
                >
                  <strong>Заблокировать</strong>
                </button>
                <button
                  className="safety-action"
                  type="button"
                  onClick={() => openSafetyView('report')}
                >
                  <strong>Пожаловаться</strong>
                  <span>Сообщить модерации о нарушении</span>
                </button>
              </div>
            )}
            {safetyView === 'report' && (
              <form
                className="safety-sheet__form"
                onSubmit={(event) => {
                  event.preventDefault();
                  setSafetyError('');
                  report.mutate();
                }}
              >
                <label>
                  Причина
                  <select
                    value={reportCategory}
                    onChange={(event) => setReportCategory(event.target.value as ReportCategory)}
                  >
                    {reportCategories.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Что произошло? <small>Необязательно</small>
                  <textarea
                    value={reportDetails}
                    onChange={(event) => setReportDetails(event.target.value)}
                    maxLength={1000}
                    placeholder="Коротко опишите ситуацию"
                  />
                </label>
                <label className="safety-sheet__check">
                  <input
                    type="checkbox"
                    checked={reportAlsoBlock}
                    onChange={(event) => setReportAlsoBlock(event.target.checked)}
                  />
                  Также заблокировать пользователя
                </label>
                <p className="hint">
                  Жалоба будет связана с этой беседой. Ваше имя пользователю не раскрывается.
                </p>
                <Button type="submit" disabled={report.isPending}>
                  {report.isPending ? 'Отправляем…' : 'Отправить жалобу'}
                </Button>
                <Button type="button" tone="ghost" onClick={() => openSafetyView('menu')}>
                  Назад
                </Button>
              </form>
            )}
            {safetyView === 'block' && (
              <div className="safety-sheet__form">
                <p>
                  Вы и {conversation.data?.counterpart.displayName ?? 'этот пользователь'} исчезнете
                  друг у друга из анкет, лайков и чатов. Уведомление не отправляется.
                </p>
                <Button tone="danger" onClick={() => block.mutate()} disabled={block.isPending}>
                  {block.isPending ? 'Блокируем…' : 'Заблокировать'}
                </Button>
                <Button tone="ghost" onClick={() => openSafetyView('menu')}>
                  Назад
                </Button>
              </div>
            )}
            {safetyView === 'unmatch' && (
              <form
                className="safety-sheet__form"
                onSubmit={(event) => {
                  event.preventDefault();
                  setSafetyError('');
                  unmatch.mutate();
                }}
              >
                <p>
                  Чат закроется для новых сообщений. Пользователь не будет заблокирован, но эта пара
                  больше не создаст новый мэтч.
                </p>
                <label>
                  Причина <small>Необязательно</small>
                  <select
                    value={unmatchReason}
                    onChange={(event) => setUnmatchReason(event.target.value)}
                  >
                    <option value="">Не указывать</option>
                    <option value="not_interested">Не хочу продолжать знакомство</option>
                    <option value="uncomfortable">Мне некомфортно</option>
                    <option value="other">Другая причина</option>
                  </select>
                </label>
                <Button tone="danger" type="submit" disabled={unmatch.isPending}>
                  {unmatch.isPending ? 'Завершаем…' : 'Завершить знакомство'}
                </Button>
                <Button type="button" tone="ghost" onClick={() => openSafetyView('menu')}>
                  Назад
                </Button>
              </form>
            )}
            {safetyView === 'mute' && (
              <div className="safety-sheet__form">
                <p>
                  {conversation.data?.muted
                    ? 'Нейтральные уведомления об этой беседе снова будут доступны. Критические уведомления безопасности не отключаются.'
                    : 'Нейтральные уведомления об этой беседе будут отключены. Мэтч и чат останутся активными, а критические уведомления безопасности сохранятся.'}
                </p>
                <Button
                  onClick={() => mute.mutate(!conversation.data?.muted)}
                  disabled={mute.isPending}
                >
                  {mute.isPending
                    ? 'Сохраняем…'
                    : conversation.data?.muted
                      ? 'Включить уведомления'
                      : 'Отключить уведомления'}
                </Button>
                <Button type="button" tone="ghost" onClick={() => openSafetyView('menu')}>
                  Назад
                </Button>
              </div>
            )}
            {safetyError && (
              <p className="form-error" role="alert">
                {safetyError}
              </p>
            )}
          </section>
        </ModalSheet>
      )}
      {(aiOpen || gamesOpen) && (
        <aside className="chat-tool-panel" aria-label="Инструменты беседы">
          {aiOpen && (
            <section className="ai-sheet" aria-label="AI-помощник">
              <div className="ai-sheet__header">
                <div>
                  <strong>PONCHIK AI</strong>
                  {aiStep === 'root' ? (
                    <p>
                      AI видит только минимальный фрагмент этой беседы для подсказки. Он ничего не
                      отправляет сам и может ошибаться.
                    </p>
                  ) : (
                    <p>Подсказки AI · отправляете только вы</p>
                  )}
                </div>
                <button
                  type="button"
                  className="icon-button"
                  aria-label="Закрыть AI-помощник"
                  onClick={() => {
                    setAiOpen(false);
                    setAiError('');
                    ai.reset();
                  }}
                >
                  ×
                </button>
              </div>
              {aiStep !== 'root' && (
                <Button
                  className="ai-sheet__back"
                  tone="ghost"
                  onClick={() => {
                    setAiStep('root');
                    setAiResult(undefined);
                    setAiExclusions([]);
                    setAiError('');
                    ai.reset();
                  }}
                >
                  ← Назад
                </Button>
              )}
              {aiStep === 'root' && (
                <div className="ai-sheet__tasks" role="group" aria-label="Задача AI">
                  <button
                    type="button"
                    onClick={() => {
                      setAiTask('reply');
                      setAiStep('reply-style');
                      setAiError('');
                      setAiResult(undefined);
                      setAiExclusions([]);
                      ai.reset();
                    }}
                  >
                    <ChatSurfaceIcon name="reply" />
                    Помочь ответить
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAiTask('interpret');
                      setAiStep('request');
                      setAiError('');
                      setAiResult(undefined);
                      setAiExclusions([]);
                      ai.reset();
                    }}
                    disabled={!aiStatus.data?.interpretEnabled || !latestIncomingMessage}
                  >
                    <ChatSurfaceIcon name="interpret" />
                    Разбор чата
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAiTask('topic');
                      setAiStep('request');
                      setAiError('');
                      setAiResult(undefined);
                      setAiExclusions([]);
                      ai.reset();
                    }}
                  >
                    <ChatSurfaceIcon name="topic" />
                    Тема
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAiOpen(false);
                      setGamesOpen(true);
                    }}
                  >
                    <ChatSurfaceIcon name="game" />
                    Игра
                  </button>
                </div>
              )}
              {aiStep === 'reply-style' && (
                <div className="ai-sheet__tones" role="group" aria-label="Стиль ответа">
                  {(['playful', 'direct', 'natural'] as const).map((tone) => (
                    <button
                      type="button"
                      className={aiTone === tone ? 'is-selected' : ''}
                      key={tone}
                      onClick={() => {
                        setAiTone(tone);
                        ai.reset();
                      }}
                    >
                      {chatAiToneLabels[tone]}
                    </button>
                  ))}
                </div>
              )}
              {aiStep === 'request' && aiTask === 'interpret' && (
                <p className="hint">
                  {aiStatus.data?.interpretEnabled
                    ? 'Разбор использует последнее входящее сообщение и не делает выводов за собеседника.'
                    : 'Разбор контекста сейчас отключён настройкой сервера.'}
                </p>
              )}
              {aiStatus.isError && (
                <>
                  <p className="form-error" role="alert">
                    Не удалось проверить доступность AI.
                  </p>
                  <Button tone="secondary" onClick={() => void aiStatus.refetch()}>
                    Повторить проверку
                  </Button>
                </>
              )}
              {aiStatus.data && !aiStatus.data.enabled && (
                <p className="hint">AI-помощник временно отключён настройкой сервера.</p>
              )}
              {aiStatus.data?.enabled && !aiStatus.data.consent.assistantUse && (
                <>
                  <p className="hint">Для подсказок нужно отдельное согласие на AI-помощника.</p>
                  <Link className="button button--secondary" to="/settings/ai">
                    Открыть настройки AI
                  </Link>
                </>
              )}
              {aiStep !== 'root' && !aiResult && (
                <Button
                  onClick={() => {
                    setAiError('');
                    ai.mutate();
                  }}
                  disabled={
                    ai.isPending ||
                    aiStatus.isLoading ||
                    !aiStatus.data?.enabled ||
                    !aiStatus.data?.consent.assistantUse ||
                    (aiTask === 'interpret' &&
                      (!aiStatus.data.interpretEnabled || !latestIncomingMessage))
                  }
                >
                  {aiStatus.isLoading
                    ? 'Проверяем доступ…'
                    : ai.isPending
                      ? 'PONCHIK думает…'
                      : aiTask === 'interpret'
                        ? 'Разбор чата'
                        : aiTask === 'topic'
                          ? 'Предложить тему'
                          : 'Получить варианты'}
                </Button>
              )}
              {aiError && (
                <>
                  <p className="form-error" role="alert">
                    {aiError}
                  </p>
                  <Button tone="secondary" onClick={() => ai.mutate()} disabled={ai.isPending}>
                    Попробовать ещё раз
                  </Button>
                </>
              )}
              {aiResult && (
                <div className="ai-sheet__suggestions">
                  <details className="ai-disclaimer">
                    <summary>Об AI-подсказках</summary>
                    <p>{aiResult.disclaimer}</p>
                  </details>
                  {aiResult.fallback ? (
                    <>
                      <p className="hint">
                        Сервис AI сейчас недоступен: показаны нейтральные варианты без анализа
                        переписки.
                      </p>
                      <Button
                        tone="secondary"
                        onClick={() => {
                          ai.reset();
                          ai.mutate();
                        }}
                      >
                        Попробовать AI снова
                      </Button>
                    </>
                  ) : null}
                  {!aiResult.fallback && aiResult.result.task === 'interpret_context' ? (
                    <ChatInterpretation result={aiResult.result} />
                  ) : !aiResult.fallback ? (
                    <>
                      <strong>
                        {aiResult.result.task === 'suggest_reply'
                          ? 'Вариант ответа'
                          : 'Вариант темы'}
                      </strong>
                      {chatAiSuggestions(aiResult.result).map((suggestion, index) => (
                        <button
                          type="button"
                          key={`${index}-${suggestion}`}
                          onClick={() => {
                            setText(suggestion);
                            updateChatDraft(conversationId, suggestion);
                            setAiOpen(false);
                            setAiStep('root');
                            setAiResult(undefined);
                            ai.reset();
                            requestAnimationFrame(() => textareaRef.current?.focus());
                          }}
                        >
                          <span>{suggestion}</span>
                          <small>Вставить в сообщение</small>
                        </button>
                      ))}
                      <Button
                        tone="secondary"
                        onClick={() => {
                          setAiResult(undefined);
                          setAiExclusions((current) =>
                            [...new Set([...current, ...chatAiSuggestions(aiResult.result)])].slice(
                              -6,
                            ),
                          );
                          ai.reset();
                          ai.mutate();
                        }}
                        disabled={ai.isPending}
                      >
                        {aiResult.result.task === 'suggest_topic'
                          ? 'Другая тема'
                          : 'Другой вариант'}
                      </Button>
                    </>
                  ) : null}
                </div>
              )}
            </section>
          )}
          {gamesOpen && (
            <GamePicker
              conversationId={conversationId}
              onClose={() => setGamesOpen(false)}
              onInvited={(game) => {
                setGamesOpen(false);
                setOpenedGameId(game.id);
                void queryClient.invalidateQueries({
                  queryKey: ['conversation-items', conversationId],
                });
              }}
            />
          )}
        </aside>
      )}
      {openedGameId && (
        <section className="game-screen" aria-label="Игра PONCHIK">
          <GameCard
            gameId={openedGameId}
            conversationId={conversationId}
            counterpartName={conversation.data?.counterpart.displayName}
            expanded
            onClose={() => {
              setDismissedGameId(openedGameId);
              setOpenedGameId(undefined);
              void queryClient.invalidateQueries({
                queryKey: ['conversation-game', conversationId],
              });
            }}
            onPlayAgain={() => {
              setDismissedGameId(openedGameId);
              setOpenedGameId(undefined);
              setGamesOpen(true);
            }}
          />
        </section>
      )}
      <div
        className="timeline"
        ref={timelineRef}
        aria-live="polite"
        onScroll={(event) => {
          updateChatScroll(conversationId, event.currentTarget.scrollTop);
          const atLatest = isChatNearLatest(event.currentTarget);
          setIsAtLatest(atLatest);
          if (atLatest) setUnseenMessages(0);
        }}
      >
        {hasOlder && (
          <button
            type="button"
            className="timeline__load-older"
            onClick={() => void loadOlder()}
            disabled={isLoadingOlder}
          >
            {isLoadingOlder ? 'Загружаем…' : 'Предыдущие сообщения'}
          </button>
        )}
        {historyError && (
          <small className="timeline__history-error" role="alert">
            {historyError}
          </small>
        )}
        {rateLimitError && <ChatRateLimitNotice error={rateLimitError} retry={retryChat} />}
        {query.isLoading ? (
          <AsyncState kind="loading" />
        ) : (query.isError || conversation.isError) &&
          !preserveRateLimitedChat &&
          !(rateLimitError && chatErrors.filter(Boolean).every(isRequestRateLimited)) ? (
          <>
            <AsyncState kind="error" />
            <Button
              tone="secondary"
              onClick={() => {
                void conversation.refetch();
                void query.refetch();
              }}
            >
              Повторить
            </Button>
          </>
        ) : rateLimitError && !preserveRateLimitedChat ? null : !query.data?.items.length ? (
          <AsyncState
            kind="empty"
            description="Начните разговор — ваше сообщение появится здесь."
          />
        ) : (
          query.data?.items.map((item, itemIndex, items) => (
            <div
              key={item.id}
              className={`bubble ${item.own ? 'bubble--own' : ''}${item.failed ? ' bubble--failed' : ''}${itemIndex > 0 && items[itemIndex - 1]?.own === item.own && items[itemIndex - 1]?.kind !== 'GAME' && item.kind !== 'GAME' ? ' bubble--grouped' : ''}`}
            >
              {item.kind === 'GAME' && item.resourceId ? (
                <GameCard
                  gameId={item.resourceId}
                  conversationId={conversationId}
                  counterpartName={conversation.data?.counterpart.displayName}
                  onOpen={() => setOpenedGameId(item.resourceId)}
                />
              ) : (
                <span className="bubble__text">{chatItemText(item)}</span>
              )}
              {item.own && item.kind !== 'GAME' ? (
                <span className="bubble__meta">
                  {chatItemTime(item.createdAt) && (
                    <small className="bubble__time">{chatItemTime(item.createdAt)}</small>
                  )}
                  <small className="bubble__status">
                    {item.failed
                      ? 'Не отправлено'
                      : item.optimistic
                        ? 'Отправляем…'
                        : item.seq <= (conversation.data?.counterpartReadSeq ?? 0)
                          ? '✓✓'
                          : '✓'}
                    {item.failed && (
                      <button
                        type="button"
                        onClick={() =>
                          send.mutate({
                            clientMessageId: item.id.replace('optimistic:', ''),
                            text: item.text ?? '',
                          })
                        }
                        disabled={send.isPending}
                      >
                        Повторить
                      </button>
                    )}
                  </small>
                </span>
              ) : (
                chatItemTime(item.createdAt) && (
                  <small className="bubble__time">{chatItemTime(item.createdAt)}</small>
                )
              )}
            </div>
          ))
        )}
      </div>
      {unseenMessages > 0 && (
        <Button
          className="chat-latest-button"
          tone="secondary"
          onClick={() => {
            if (timelineRef.current) scrollChatToLatest(timelineRef.current);
            setIsAtLatest(true);
            setUnseenMessages(0);
          }}
        >
          К новым сообщениям
        </Button>
      )}
      {sendError && (
        <p className="form-error" role="alert">
          Не удалось отправить сообщение. Его можно повторить.
        </p>
      )}
      <form
        className="composer"
        onSubmit={(event) => {
          event.preventDefault();
          const message = text.trim();
          if (!message || send.isPending) return;
          const input =
            retryMessage?.text === message
              ? retryMessage
              : { clientMessageId: crypto.randomUUID(), text: message };
          setText('');
          updateChatDraft(conversationId, '');
          textareaRef.current?.style.removeProperty('height');
          send.mutate(input);
        }}
      >
        <button
          type="button"
          aria-label="AI-помощник"
          title="AI-помощник"
          onClick={() => {
            setAiOpen(true);
            setAiError('');
            ai.reset();
          }}
        >
          <ChatSurfaceIcon name="spark" />
        </button>
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(event) => {
            setText(event.target.value);
            updateChatDraft(conversationId, event.target.value);
            if (retryMessage?.text !== event.target.value.trim()) setRetryMessage(undefined);
          }}
          onKeyDown={(event) => {
            const hasFinePointer =
              typeof window.matchMedia === 'function'
                ? window.matchMedia('(pointer: fine)').matches
                : true;
            if (
              !shouldSubmitChatComposer({
                key: event.key,
                shiftKey: event.shiftKey,
                isComposing: event.nativeEvent.isComposing,
                hasFinePointer,
              })
            )
              return;
            event.preventDefault();
            event.currentTarget.form?.requestSubmit();
          }}
          onInput={(event) => {
            const target = event.currentTarget;
            target.style.height = 'auto';
            target.style.height = `${Math.min(target.scrollHeight, 120)}px`;
          }}
          maxLength={2000}
          rows={1}
          placeholder="Сообщение"
          aria-label="Сообщение"
        />
        <button
          type="submit"
          disabled={send.isPending || !text.trim()}
          aria-label="Отправить"
          onPointerDown={(event) => {
            if (document.activeElement === textareaRef.current) event.preventDefault();
          }}
        >
          <ChatSurfaceIcon name="send" />
        </button>
      </form>
    </Screen>
  );
}

function GamePicker({
  conversationId,
  onClose,
  onInvited,
}: {
  conversationId: string;
  onClose: () => void;
  onInvited: (game: ChatGame) => void;
}) {
  const catalog = useQuery({
    queryKey: ['games-catalog'],
    queryFn: () => api<{ types: GameCatalogEntry[] }>('/games/catalog'),
  });
  const [error, setError] = useState('');
  const [confirmFlirt, setConfirmFlirt] = useState(false);
  const invite = useMutation({
    mutationFn: async ({
      gameType,
      flirtConsent,
    }: {
      gameType: GameCatalogEntry['id'];
      flirtConsent?: boolean;
    }) => {
      try {
        return await api<ChatGame>(`/conversations/${conversationId}/games`, {
          method: 'POST',
          body: JSON.stringify(gameInviteBody(gameType, flirtConsent)),
        });
      } catch (reason) {
        if (reason instanceof ApiClientError && reason.code === 'GAME_STATE_CONFLICT') {
          const canonical = await api<{ game: ChatGame | null }>(
            `/conversations/${conversationId}/games/current`,
          );
          if (canonical.game) return canonical.game;
        }
        throw reason;
      }
    },
    onSuccess: onInvited,
    onError: (reason: unknown) =>
      setError(
        reason instanceof ApiClientError ? reason.message : 'Не удалось отправить приглашение',
      ),
  });
  return (
    <section className="game-sheet" aria-label="Игры в чате">
      <div className="game-sheet__intro">
        <span className="game-sheet__mark" aria-hidden="true">
          <GameSurfaceIcon gameType="questions" />
        </span>
        <div>
          <strong>Игры PONCHIK</strong>
          <p>Выберите настроение — игра начнётся только после согласия собеседника.</p>
        </div>
      </div>
      {catalog.isLoading ? <p className="hint">Загружаем игры…</p> : null}
      {catalog.isError ? (
        <>
          <p className="form-error" role="alert">
            Не удалось загрузить доступные игры.
          </p>
          <Button tone="secondary" onClick={() => void catalog.refetch()}>
            Повторить
          </Button>
        </>
      ) : null}
      {!catalog.isLoading && !catalog.isError && !catalog.data?.types.length ? (
        <p className="hint">Сейчас нет доступных игр.</p>
      ) : null}
      {confirmFlirt ? (
        <FlirtConsent
          pending={invite.isPending}
          onAccept={() => invite.mutate({ gameType: 'flirt', flirtConsent: true })}
          onDecline={() => setConfirmFlirt(false)}
        />
      ) : (
        <div className="game-picker">
          {catalog.data?.types.map((game) => (
            <button
              className="game-picker__item"
              data-game-type={game.id}
              key={game.id}
              type="button"
              disabled={invite.isPending}
              onClick={() => {
                setError('');
                if (game.id === 'flirt') setConfirmFlirt(true);
                else invite.mutate({ gameType: game.id });
              }}
            >
              <span className="game-picker__icon" aria-hidden="true">
                <GameSurfaceIcon gameType={game.id} />
              </span>
              <span className="game-picker__copy">
                <strong>{game.title}</strong>
                <small>{gameSubtitle(game.id)}</small>
              </span>
              <span className="game-picker__arrow" aria-hidden="true">
                ›
              </span>
            </button>
          ))}
        </div>
      )}
      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
      <Button tone="secondary" onClick={onClose}>
        Закрыть
      </Button>
    </section>
  );
}

function gameSubtitle(gameType: GameCatalogEntry['id']) {
  return {
    questions: 'Пять поводов узнать друг друга',
    never_have_i_ever: 'Истории, которые хочется раскрыть',
    most_likely: 'Проверьте, как вы видите друг друга',
    would_you_rather: 'Выбор без правильных ответов',
    guess_fact: 'Сможете отличить правду от вымысла?',
    compatibility: 'Насколько вы похожи?',
    flirt: 'Близость, желания и личные границы',
  }[gameType];
}

function GameSurfaceIcon({ gameType }: { gameType: GameCatalogEntry['id'] }) {
  return (
    <svg className="game-surface-icon" viewBox="0 0 24 24" aria-hidden="true">
      {gameType === 'never_have_i_ever' && <path d="M7 4h10l2 5-7 11L5 9l2-5Zm-2 5h14" />}
      {gameType === 'most_likely' && (
        <>
          <circle cx="8" cy="8" r="3" />
          <circle cx="17" cy="9" r="2.5" />
          <path d="M3 20c.4-4 2-6 5-6s4.6 2 5 6m1-5c3 0 4.5 1.7 5 5" />
        </>
      )}
      {gameType === 'would_you_rather' && <path d="m8 5-5 7 5 7m8-14 5 7-5 7M3 12h18" />}
      {gameType === 'guess_fact' && (
        <>
          <path d="M12 21a9 9 0 1 0-9-9 9 9 0 0 0 9 9Z" />
          <path d="M9.5 9a2.7 2.7 0 1 1 4.5 2c-1.2.8-2 1.2-2 2.5M12 17h.01" />
        </>
      )}
      {gameType === 'compatibility' && (
        <>
          <path d="M12 20S4 15.5 4 9.5A4.5 4.5 0 0 1 12 6a4.5 4.5 0 0 1 8 3.5C20 15.5 12 20 12 20Z" />
          <path d="m8.5 11 2.2 2.2 4.8-5" />
        </>
      )}
      {gameType === 'flirt' && (
        <>
          <path d="M12 20S4 15.5 4 9.5A4.5 4.5 0 0 1 12 6a4.5 4.5 0 0 1 8 3.5C20 15.5 12 20 12 20Z" />
          <path d="m18 3 .7 2.3L21 6l-2.3.7L18 9l-.7-2.3L15 6l2.3-.7L18 3Z" />
        </>
      )}
      {gameType === 'questions' && (
        <>
          <path d="M21 15a4 4 0 0 1-4 4H9l-5 3v-5a7 7 0 1 1 17-2Z" />
          <path d="M9.5 9a2.7 2.7 0 1 1 4.5 2c-1.2.8-2 1.2-2 2.5" />
        </>
      )}
    </svg>
  );
}

function GameCard({
  gameId,
  conversationId,
  counterpartName,
  expanded = false,
  onOpen,
  onClose,
  onPlayAgain,
}: {
  gameId: string;
  conversationId: string;
  counterpartName: string | undefined;
  expanded?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  onPlayAgain?: () => void;
}) {
  const queryClient = useQueryClient();
  const game = useQuery({
    queryKey: ['game', gameId],
    queryFn: () => api<ChatGame>(`/games/${gameId}`),
    refetchInterval: (query) =>
      ['INVITED', 'ACTIVE'].includes(query.state.data?.status ?? '') ? 4000 : false,
  });
  const [answer, setAnswer] = useState('');
  const [factTruth, setFactTruth] = useState(true);
  const [error, setError] = useState('');
  const [confirmEnd, setConfirmEnd] = useState(false);
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['game', gameId] });
    await queryClient.invalidateQueries({ queryKey: ['conversation-items', conversationId] });
  };
  const action = useMutation({
    mutationFn: async (input: { path: string; body?: object }) =>
      api<ChatGame>(input.path, {
        method: 'POST',
        ...(input.body ? { body: JSON.stringify(input.body) } : {}),
      }),
    onSuccess: () => void refresh(),
    onError: (reason: unknown) =>
      setError(reason instanceof ApiClientError ? reason.message : 'Не удалось обновить игру'),
  });
  if (game.isLoading)
    return expanded ? (
      <p className="game-screen__loading">Готовим игру…</p>
    ) : (
      <span>Игра готовится…</span>
    );
  if (!game.data)
    return expanded ? (
      <div className="game-screen__empty">
        <p>Эта игра уже завершена или недоступна.</p>
        <Button onClick={onClose}>Вернуться в чат</Button>
      </div>
    ) : (
      <span>Игра недоступна</span>
    );
  const value = game.data;
  if (!expanded)
    return (
      <button
        type="button"
        className="game-timeline-card"
        data-game-id={value.id}
        data-game-type={value.gameType}
        onClick={onOpen}
      >
        <span className="game-timeline-card__icon" aria-hidden="true">
          <GameSurfaceIcon gameType={value.gameType} />
        </span>
        <strong>{gameStatusText(value.status)}</strong>
        <span>{value.status === 'COMPLETED' ? 'Посмотреть результат' : 'Открыть игру'}</span>
      </button>
    );
  const latestRevealedRound = value.roundHistory.at(-1) ?? null;
  const justRevealedRound =
    value.status === 'COMPLETED' && value.round?.state === 'REVEALED'
      ? value.round
      : value.status === 'ACTIVE'
        ? latestRevealedRound
        : null;
  const submitAnswer = () => {
    if (!answer.trim() || !value.round) return;
    setError('');
    action.mutate({
      path: `/games/${value.id}/rounds/${value.round.id}/answer`,
      body: { answer: answer.trim(), version: value.version },
    });
    setAnswer('');
  };
  const submitQuickAnswer = (quickAnswer: string) => {
    if (!value.round) return;
    setError('');
    action.mutate({
      path: `/games/${value.id}/rounds/${value.round.id}/answer`,
      body: { answer: quickAnswer, version: value.version },
    });
  };
  const submitGuessFact = (body: object) => {
    if (!value.round) return;
    setError('');
    action.mutate({
      path: `/games/${value.id}/rounds/${value.round.id}/answer`,
      body: { ...body, version: value.version },
    });
    setAnswer('');
  };
  return (
    <section
      className={`game-card game-card--expanded${value.gameType === 'flirt' ? ' game-card--flirt' : ''}`}
      data-game-type={value.gameType}
      aria-label="Игра"
    >
      <header className="game-screen__header">
        <Button tone="ghost" onClick={onClose}>
          ← В чат
        </Button>
        <span className="game-card__status">{gameStatusText(value.status)}</span>
      </header>
      <div className="game-card__identity">
        <span aria-hidden="true">
          <GameSurfaceIcon gameType={value.gameType} />
        </span>
        <div>
          <small>PONCHIK · ИГРА</small>
          <strong className="game-card__title">{gameTitle(value.gameType)}</strong>
        </div>
      </div>
      {value.status === 'ACTIVE' && value.round ? (
        <div className="game-card__progress" aria-label={`Раунд ${value.round.number}`}>
          <span>
            Раунд {value.round.number} из {gameRoundLimit(value.gameType)}
          </span>
          <span className="game-card__progress-track" aria-hidden="true">
            {Array.from({ length: gameRoundLimit(value.gameType) }, (_, index) => (
              <i key={index} className={index < value.round!.number ? 'is-complete' : ''} />
            ))}
          </span>
        </div>
      ) : null}
      {justRevealedRound ? (
        <details className="game-card__recent-result">
          <summary>Раунд {justRevealedRound.number} — посмотреть ответы</summary>
          <p>
            Раунд {justRevealedRound.number}: {gamePromptText(justRevealedRound.prompt)}
          </p>
          <RevealedGameAnswers
            round={justRevealedRound}
            counterpartName={counterpartName}
            title={`Ответы раунда ${justRevealedRound.number}`}
          />
        </details>
      ) : null}
      {value.status === 'ACTIVE' && value.round?.state === 'OPEN' ? (
        <section className="game-card__prompt">
          <small>ВАШ ВОПРОС</small>
          <h2>{gamePromptText(value.round.prompt)}</h2>
        </section>
      ) : !value.round && value.status === 'INVITED' ? (
        <p>Выберите действие, чтобы продолжить.</p>
      ) : null}
      {value.viewerActions.accept ? (
        value.gameType === 'flirt' ? (
          <FlirtConsent
            pending={action.isPending}
            onAccept={() =>
              action.mutate({
                path: `/games/${value.id}/accept`,
                body: { version: value.version, flirtConsent: true },
              })
            }
            onDecline={() => action.mutate({ path: `/games/${value.id}/decline` })}
          />
        ) : (
          <div className="game-card__actions">
            <Button
              onClick={() =>
                action.mutate({
                  path: `/games/${value.id}/accept`,
                  body: { version: value.version },
                })
              }
            >
              Принять
            </Button>
            <Button
              tone="secondary"
              onClick={() => action.mutate({ path: `/games/${value.id}/decline` })}
            >
              Отклонить
            </Button>
          </div>
        )
      ) : null}
      {value.gameType === 'flirt' && value.status === 'INVITED' && value.flirtRulesVersion !== 2 ? (
        <section>
          <p className="hint">
            Это старое приглашение. Отмените или отклоните его и отправьте новое с подтверждением
            обоих участников.
          </p>
          {value.viewerActions.decline && !value.viewerActions.accept ? (
            <Button
              tone="secondary"
              disabled={action.isPending}
              onClick={() => action.mutate({ path: `/games/${value.id}/decline` })}
            >
              Отклонить старое приглашение
            </Button>
          ) : null}
        </section>
      ) : null}
      {value.gameType === 'flirt' && value.status === 'ACTIVE' ? (
        <p className="hint">{flirtWarning}</p>
      ) : null}
      {value.viewerActions.cancel ? (
        <Button
          tone="secondary"
          onClick={() => action.mutate({ path: `/games/${value.id}/cancel` })}
        >
          Отменить приглашение
        </Button>
      ) : null}
      {value.status === 'ACTIVE' &&
      value.round?.state === 'OPEN' &&
      value.gameType === 'guess_fact' &&
      value.round.guessFact ? (
        <GuessFactTurn
          round={value.round}
          statement={answer}
          truth={factTruth}
          pending={action.isPending}
          onStatementChange={setAnswer}
          onTruthChange={setFactTruth}
          onSubmit={submitGuessFact}
        />
      ) : null}
      {value.status === 'ACTIVE' &&
      value.round?.state === 'OPEN' &&
      value.gameType !== 'guess_fact' &&
      !value.round.answeredByMe ? (
        gameQuickAnswers(value.gameType, value.round.prompt) ? (
          <div className="game-card__quick-answers" role="group" aria-label="Выберите ответ">
            {gameQuickAnswers(value.gameType, value.round.prompt)?.map((quickAnswer) => (
              <Button
                key={quickAnswer}
                tone="secondary"
                onClick={() => submitQuickAnswer(quickAnswer)}
                disabled={action.isPending}
              >
                {quickAnswer}
              </Button>
            ))}
          </div>
        ) : (
          <div className="game-card__answer">
            <input
              value={answer}
              onChange={(event) => setAnswer(event.target.value)}
              maxLength={500}
              placeholder="Ваш ответ"
              aria-label="Ваш ответ в игре"
            />
            <Button onClick={submitAnswer} disabled={!answer.trim() || action.isPending}>
              Ответить
            </Button>
          </div>
        )
      ) : null}
      {value.status === 'ACTIVE' &&
      value.round?.state === 'OPEN' &&
      value.gameType !== 'guess_fact' &&
      value.round.answeredByMe ? (
        <section className="game-card__waiting" role="status">
          <span className="game-card__partner-avatar" aria-hidden="true">
            {counterpartName?.trim().charAt(0).toUpperCase() || '♡'}
          </span>
          <div>
            <strong>Ждём ответ партнёра</strong>
            <p>Ваш ответ сохранён и останется скрытым до общего раскрытия.</p>
          </div>
          <i aria-hidden="true" />
        </section>
      ) : null}
      {value.gameType === 'flirt' &&
      value.status === 'ACTIVE' &&
      value.round?.state === 'OPEN' &&
      !value.round.answeredByMe ? (
        <Button
          tone="secondary"
          disabled={action.isPending}
          onClick={() => {
            submitQuickAnswer('Пропускаю этот вопрос');
            setAnswer('');
          }}
        >
          Пропустить вопрос
        </Button>
      ) : null}
      {value.viewerActions.end ? (
        confirmEnd ? (
          <section className="game-card__confirm" aria-label="Подтверждение завершения игры">
            <strong>Завершить игру?</strong>
            <div className="game-card__actions">
              <Button tone="secondary" onClick={() => setConfirmEnd(false)}>
                Отмена
              </Button>
              <Button
                tone="danger"
                disabled={action.isPending}
                onClick={() =>
                  action.mutate(
                    { path: `/games/${value.id}/cancel` },
                    { onSuccess: () => setConfirmEnd(false) },
                  )
                }
              >
                Завершить
              </Button>
            </div>
          </section>
        ) : (
          <Button className="game-card__end" tone="ghost" onClick={() => setConfirmEnd(true)}>
            Завершить игру
          </Button>
        )
      ) : null}
      {value.status === 'COMPLETED' && value.roundHistory.length > 0 ? (
        <details className="game-card__history">
          <summary>{gameHistoryTitle(value.roundHistory.length)}</summary>
          <div className="game-card__history-list">
            {value.roundHistory.map((historyRound) => (
              <section key={historyRound.id} className="game-card__history-round">
                <strong>
                  Раунд {historyRound.number}: {gamePromptText(historyRound.prompt)}
                </strong>
                <RevealedGameAnswers
                  round={historyRound}
                  counterpartName={counterpartName}
                  title={`Ответы раунда ${historyRound.number}`}
                />
              </section>
            ))}
          </div>
        </details>
      ) : null}
      {value.status === 'COMPLETED' && value.compatibilityResult ? (
        <section className="game-card__compatibility" aria-label="Итог совместимости">
          <div
            className="compatibility-score"
            style={
              {
                '--compatibility': `${value.compatibilityResult.compatibilityPercent}%`,
              } as React.CSSProperties
            }
          >
            <span>
              <strong>{value.compatibilityResult.compatibilityPercent}%</strong>
              <small>совместимость</small>
            </span>
          </div>
          <div className="compatibility-result__headline">
            <small>ВАШ РЕЗУЛЬТАТ</small>
            <h2>{value.compatibilityResult.shortVerdict}</h2>
          </div>
          <section className="compatibility-result__section compatibility-result__section--strengths">
            <strong>Ваши сильные стороны</strong>
            <ul>
              {value.compatibilityResult.strengths.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>
          {value.compatibilityResult.differences.length > 0 ? (
            <section className="compatibility-result__section">
              <strong>Различия</strong>
              <ul>
                {value.compatibilityResult.differences.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
          ) : null}
          <section className="compatibility-result__advice">
            <strong>Совет от PONCHIK</strong>
            <p>{value.compatibilityResult.recommendation}</p>
          </section>
          <small className="compatibility-result__disclaimer">
            {value.compatibilityResult.disclaimer}
          </small>
          {value.compatibilityResultSource === 'FALLBACK' ? (
            <small>AI был недоступен, поэтому итог рассчитан по совпадениям ответов.</small>
          ) : null}
        </section>
      ) : null}
      {value.status === 'COMPLETED' &&
      value.gameType === 'compatibility' &&
      !value.compatibilityResult ? (
        <p className="game-card__result-loading" role="status">
          Собираем ваш общий результат…
        </p>
      ) : null}
      {value.status === 'COMPLETED' ? (
        <div className="game-card__actions">
          <Button onClick={onClose}>Вернуться в чат</Button>
          <Button tone="secondary" onClick={onPlayAgain}>
            Сыграть ещё
          </Button>
        </div>
      ) : null}
      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}

function GuessFactTurn({
  round,
  statement,
  truth,
  pending,
  onStatementChange,
  onTruthChange,
  onSubmit,
}: {
  round: NonNullable<ChatGame['round']>;
  statement: string;
  truth: boolean;
  pending: boolean;
  onStatementChange: (value: string) => void;
  onTruthChange: (value: boolean) => void;
  onSubmit: (body: object) => void;
}) {
  const fact = round.guessFact;
  if (!fact) return null;
  if (fact.role === 'AUTHOR') {
    if (round.answeredByMe)
      return (
        <p className="hint">
          Утверждение отправлено. Собеседник сейчас угадывает, правда это или вымысел.
        </p>
      );
    return (
      <div className="game-card__guess-fact">
        <strong>{guessFactRoleText(fact.role)}</strong>
        <p className="hint">Без контактов, адресов и чужих личных данных.</p>
        <textarea
          value={statement}
          onChange={(event) => onStatementChange(event.target.value)}
          maxLength={180}
          placeholder="Например: я однажды ночевал(а) в палатке зимой"
          aria-label="Утверждение для игры"
        />
        <div className="game-card__actions">
          <Button tone={truth ? 'primary' : 'secondary'} onClick={() => onTruthChange(true)}>
            Это правда
          </Button>
          <Button tone={!truth ? 'primary' : 'secondary'} onClick={() => onTruthChange(false)}>
            Это вымысел
          </Button>
        </div>
        <Button
          onClick={() => onSubmit({ statement: statement.trim(), truth })}
          disabled={statement.trim().length < 5 || pending}
        >
          Отправить утверждение
        </Button>
      </div>
    );
  }
  if (!fact.statement)
    return <p className="hint">{guessFactRoleText(fact.role)}: ждём утверждение собеседника.</p>;
  if (round.answeredByMe)
    return <p className="hint">Ваше предположение сохранено. Ждём раскрытия ответа.</p>;
  return (
    <div className="game-card__guess-fact">
      <strong>{guessFactRoleText(fact.role)}</strong>
      <p className="game-card__claim">«{fact.statement}»</p>
      <div className="game-card__actions">
        <Button onClick={() => onSubmit({ guess: 'TRUE' })} disabled={pending}>
          Правда
        </Button>
        <Button tone="secondary" onClick={() => onSubmit({ guess: 'FICTION' })} disabled={pending}>
          Вымысел
        </Button>
      </div>
    </div>
  );
}

function RevealedGameAnswers({
  round,
  counterpartName,
  title,
}: {
  round: NonNullable<ChatGame['round']>;
  counterpartName: string | undefined;
  title: string;
}) {
  const factResult = round.guessFact?.result;
  if (factResult) {
    const author = factResult.authorOwn ? 'Вы' : counterpartName?.trim() || 'Собеседник';
    const guesser = factResult.authorOwn ? counterpartName?.trim() || 'Собеседник' : 'Вы';
    return (
      <div className="game-card__fact-result">
        <strong>{title}</strong>
        <div className="game-card__fact-statement">
          <small>Факт — {author}</small>
          <span>«{factResult.statement}»</span>
        </div>
        <div className="game-card__fact-row">
          <small>Правильный ответ</small>
          <strong className={factResult.truth ? 'fact-value--true' : 'fact-value--fiction'}>
            {factResult.truth ? '✓ Правда' : '✦ Вымысел'}
          </strong>
        </div>
        <div className="game-card__fact-row">
          <small>Предположение — {guesser}</small>
          <strong>{factResult.guess === 'TRUE' ? 'Правда' : 'Вымысел'}</strong>
        </div>
        <div
          className={`game-card__fact-outcome ${factResult.guessedCorrectly ? 'is-correct' : 'is-wrong'}`}
        >
          {factResult.guessedCorrectly ? `🎉 ${guesser} угадал(а)` : `🙂 ${guesser} не угадал(а)`}
        </div>
      </div>
    );
  }
  return (
    <div className="game-card__answers">
      <strong>{title}</strong>
      {round.answers?.map((item, index) => (
        <span key={`${item.own ? 'own' : 'counterpart'}-${index}`}>
          <b>{gameAnswerLabel(item.own, counterpartName)}:</b> {item.answer ?? 'Ответ недоступен'}
        </span>
      ))}
    </div>
  );
}

export function ProfileEditPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const markDirty = () => {
    dirtyRef.current = true;
    setDirty(true);
  };
  const blocker = useBlocker(() => dirtyRef.current);
  useEffect(() => {
    if (!dirty) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    getTelegram()?.enableClosingConfirmation?.();
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      getTelegram()?.disableClosingConfirmation?.();
    };
  }, [dirty]);
  const profile = useQuery({
    queryKey: ['profile-edit'],
    queryFn: () => api<PrivateProfile>('/me/profile'),
    refetchOnWindowFocus: false,
  });
  const cities = useQuery({
    queryKey: ['cities'],
    queryFn: () => api<CityOption[]>('/reference/cities'),
  });
  const [birthDate, setBirthDate] = useState('');
  const [cityId, setCityId] = useState('');
  const [countryCode, setCountryCode] = useState('');
  useEffect(() => {
    if (!profile.data || dirtyRef.current) return;
    setBirthDate(profile.data.birthDate ?? '');
    setCityId(profile.data.public?.cityId ?? '');
    setCountryCode(profile.data.public?.countryCode ?? '');
  }, [profile.data]);
  const save = useMutation({
    mutationFn: async (form: HTMLFormElement) => {
      const values = Object.fromEntries(new FormData(form).entries());
      const version = profile.data?.version ?? 0;
      const basics = await api<{ version: number }>('/me/profile/basics', {
        method: 'PUT',
        body: JSON.stringify({
          displayName: values.displayName,
          birthDate: values.birthDate,
          gender: values.gender,
          version,
        }),
      });
      const location = await api<{ version: number }>('/me/profile/location', {
        method: 'PUT',
        body: JSON.stringify({ cityId, countryCode, version: basics.version }),
      });
      await api('/me/profile/about', {
        method: 'PUT',
        body: JSON.stringify({
          bio: values.bio,
          work: values.work,
          education: values.education,
          hobbies: values.hobbies,
          pets: values.pets ? [values.pets] : [],
          favoriteFilm: values.favoriteFilm,
          favoriteMusic: values.favoriteMusic,
          goal: values.goal || null,
          interestIds: [],
          version: location.version,
        }),
      });
    },
    onSuccess: () => {
      dirtyRef.current = false;
      setDirty(false);
      void finishProfileEditSave(queryClient, navigate);
    },
  });
  if (profile.isLoading || cities.isLoading)
    return (
      <Screen title="Редактировать профиль">
        <AsyncState kind="loading" />
      </Screen>
    );
  if (profile.isError || cities.isError || !profile.data)
    return (
      <Screen title="Редактировать профиль">
        <AsyncState kind="error" />
      </Screen>
    );
  const value = profile.data.public ?? {};
  return (
    <Screen title="Редактировать профиль" eyebrow="Моя анкета" className="screen--profile-edit">
      {blocker.state === 'blocked' && (
        <ModalSheet label="Несохранённые изменения" onClose={() => blocker.reset()}>
          <h2>Выйти без сохранения?</h2>
          <p>Изменения в анкете не будут сохранены.</p>
          <Button tone="secondary" onClick={() => blocker.reset()}>
            Продолжить редактирование
          </Button>
          <Button tone="danger" disabled={save.isPending} onClick={() => blocker.proceed()}>
            Выйти без сохранения
          </Button>
        </ModalSheet>
      )}
      <form
        className={`form profile-edit-form${dirty ? ' profile-edit-form--dirty' : ''}`}
        onChange={markDirty}
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate(event.currentTarget);
        }}
      >
        <section className="profile-edit-section">
          <div className="profile-edit-section__heading">
            <span className="settings-row__icon">
              <ProfileSettingsIcon name="edit" />
            </span>
            <div>
              <h2>Основное</h2>
              <p>То, что помогает узнать вас с первого взгляда.</p>
            </div>
          </div>
          <Field label="Имя">
            <Input
              name="displayName"
              required
              minLength={2}
              maxLength={40}
              defaultValue={value.displayName ?? ''}
            />
          </Field>
          <Field label="Дата рождения">
            <DateWheel
              value={birthDate}
              onChange={(value) => {
                setBirthDate(value);
                markDirty();
              }}
            />
          </Field>
          <Field label="Пол">
            <Select name="gender" defaultValue={value.gender ?? 'prefer_not_to_say'}>
              <option value="woman">Женщина</option>
              <option value="man">Мужчина</option>
              <option value="prefer_not_to_say">Не указывать</option>
            </Select>
          </Field>
          <CityPicker
            required
            countryCode={countryCode}
            onCountryChange={(code) => {
              setCountryCode(code);
              setCityId('');
              markDirty();
            }}
            value={cityId}
            onChange={(value) => {
              setCityId(value);
              markDirty();
            }}
            cities={cities.data ?? []}
          />
          <Field label="Цель знакомства">
            <Select name="goal" defaultValue={value.goal ?? ''}>
              <option value="">Не указывать</option>
              <option value="relationship">Отношения</option>
              <option value="communication">Общение</option>
              <option value="flirt">Флирт</option>
              <option value="unsure">Пока не знаю</option>
            </Select>
          </Field>
        </section>
        <section className="profile-edit-section">
          <div className="profile-edit-section__heading">
            <span className="settings-row__icon">
              <ProfileSettingsIcon name="photos" />
            </span>
            <div>
              <h2>О вас</h2>
              <p>Несколько деталей делают анкету живой.</p>
            </div>
          </div>
          <Field label="О себе">
            <Textarea name="bio" rows={5} maxLength={500} defaultValue={value.bio ?? ''} />
          </Field>
          <Field label="Работа">
            <Input name="work" defaultValue={value.work ?? ''} />
          </Field>
          <Field label="Учёба">
            <Input name="education" defaultValue={value.education ?? ''} />
          </Field>
          <Field label="Хобби">
            <Input name="hobbies" defaultValue={value.hobbies ?? ''} />
          </Field>
          <Field label="Домашние животные">
            <Select name="pets" defaultValue={value.pets?.[0] ?? ''}>
              <option value="">Не указывать</option>
              <option value="cat">Кошка</option>
              <option value="dog">Собака</option>
              <option value="other">Другие</option>
              <option value="none">Нет</option>
            </Select>
          </Field>
          <Field label="Любимый фильм / сериал">
            <Input name="favoriteFilm" defaultValue={value.favoriteFilm ?? ''} />
          </Field>
          <Field label="Любимая музыка">
            <Input name="favoriteMusic" defaultValue={value.favoriteMusic ?? ''} />
          </Field>
        </section>
        {save.isError && (
          <p className="form-error">Не удалось сохранить профиль. Повторите попытку.</p>
        )}
        <Button
          type="submit"
          disabled={
            save.isPending ||
            !dirty ||
            !cities.data?.some((city) => city.id === cityId && city.countryCode === countryCode)
          }
          className="profile-edit-form__submit"
        >
          {save.isPending ? 'Сохраняем…' : 'Сохранить'}
        </Button>
      </form>
    </Screen>
  );
}

export function SearchSettingsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const profile = useQuery({
    queryKey: ['search-profile'],
    queryFn: () => api<PrivateProfile>('/me/profile'),
    refetchOnWindowFocus: false,
  });
  const cities = useQuery({
    queryKey: ['cities'],
    queryFn: () => api<CityOption[]>('/reference/cities'),
  });
  const [cityId, setCityId] = useState('');
  const [countryCode, setCountryCode] = useState('');
  useEffect(() => {
    if (profile.data) {
      setCityId(profile.data.preferences?.cityId ?? '');
      setCountryCode(profile.data.preferences?.countryCode ?? '');
    }
  }, [profile.data]);
  const save = useMutation({
    mutationFn: (form: HTMLFormElement) => {
      const values = Object.fromEntries(new FormData(form).entries());
      return api('/me/search-preferences', {
        method: 'PUT',
        body: JSON.stringify({
          seeking: values.seeking === 'all' ? ['woman', 'man'] : [values.seeking],
          ageMin: Number(values.ageMin),
          ageMax: Number(values.ageMax),
          cityId: values.cityId || null,
          countryCode: values.countryCode,
          goal: values.goal || null,
          regionFallback: false,
          introEnabled: true,
          version: profile.data?.version ?? 0,
        }),
      });
    },
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: ['discovery'] });
      await invalidateOwnerProfileQueries(queryClient);
      void navigate('/discover');
    },
  });
  if (profile.isLoading || cities.isLoading)
    return (
      <Screen title="Настройки поиска">
        <AsyncState kind="loading" />
      </Screen>
    );
  if (profile.isError || cities.isError || !profile.data)
    return (
      <Screen title="Настройки поиска">
        <AsyncState kind="error" />
      </Screen>
    );
  const preferences = profile.data.preferences ?? {};
  const seeking =
    preferences.seekingGenders?.length === 2 ? 'all' : (preferences.seekingGenders?.[0] ?? 'all');
  return (
    <Screen title="Настройки поиска">
      <form
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          const form = event.currentTarget;
          const input = form.elements.namedItem('cityId') as HTMLInputElement | null;
          if (input) input.value = cityId;
          save.mutate(form);
        }}
      >
        <Field label="Кого показывать">
          <select name="seeking" defaultValue={seeking}>
            <option value="woman">Женщин</option>
            <option value="man">Мужчин</option>
            <option value="all">Всех</option>
          </select>
        </Field>
        <div className="field-row">
          <Field label="Возраст от">
            <input
              name="ageMin"
              type="number"
              min={18}
              max={99}
              defaultValue={preferences.ageMin ?? 18}
            />
          </Field>
          <Field label="до">
            <input
              name="ageMax"
              type="number"
              min={18}
              max={99}
              defaultValue={preferences.ageMax ?? 99}
            />
          </Field>
        </div>
        <CityPicker
          required
          value={cityId}
          onChange={setCityId}
          cities={cities.data ?? []}
          countryCode={countryCode}
          onCountryChange={(code) => {
            setCountryCode(code);
            setCityId('');
          }}
          countryLabel="Страна поиска"
          cityLabel="Город поиска"
        />
        <Field label="Цель знакомства">
          <select name="goal" defaultValue={preferences.goal ?? ''}>
            <option value="">Любая</option>
            <option value="relationship">Отношения</option>
            <option value="communication">Общение</option>
            <option value="flirt">Флирт</option>
            <option value="unsure">Пока не знаю</option>
          </select>
        </Field>
        {save.isError && (
          <p className="form-error">Не удалось сохранить настройки. Повторите попытку.</p>
        )}
        <p className="hint">Временное расширение поиска на 24 часа не изменяет эти настройки.</p>
        <Button
          type="submit"
          disabled={
            save.isPending ||
            !cities.data?.some((city) => city.id === cityId && city.countryCode === countryCode)
          }
        >
          {save.isPending ? 'Сохраняем…' : 'Сохранить'}
        </Button>
      </form>
    </Screen>
  );
}

export function ProfilePage() {
  const query = useQuery({
    queryKey: ['profile'],
    queryFn: () =>
      api<{ public?: ProfileCard; moderationStatus: string; completionPercent: number }>(
        '/me/profile',
      ),
  });
  const profile = query.data?.public;
  const completion = query.data?.completionPercent ?? 0;
  const interests = profile ? profileInterests(profile, 8) : [];
  const details = profile
    ? [
        ['Работа', profile.details?.work],
        ['Учёба', profile.details?.education],
        ['Смотрит', profile.details?.favoriteFilm],
        ['Слушает', profile.details?.favoriteMusic],
      ].filter((item): item is [string, string] => Boolean(item[1]?.trim()))
    : [];
  return (
    <Screen title="Моя анкета" className="screen--profile">
      {query.isLoading ? (
        <AsyncState kind="loading" />
      ) : (
        <>
          {profile && (
            <Link className="owner-profile-card" to="/profile/me" aria-label="Открыть мою анкету">
              <span className="owner-profile-card__photo">
                <Avatar
                  label={profile.displayName.slice(0, 1)}
                  assetId={profile.avatarAssetId}
                  decorative
                />
                <span>Открыть</span>
              </span>
              <div className="owner-profile-card__identity">
                <small>МОЯ АНКЕТА</small>
                <h2>
                  {profile.displayName}, {profile.age}
                </h2>
                <p>⌖ {[profile.city, profile.country].filter(Boolean).join(', ')}</p>
                {profile.goal ? (
                  <span className="owner-profile-card__goal">{profileGoalLabel(profile.goal)}</span>
                ) : null}
              </div>
            </Link>
          )}
          {completion < 100 && (
            <Link
              className="profile-completion"
              to="/profile/edit"
              aria-label={`Анкета заполнена на ${completion}%`}
            >
              <div className="profile-completion__copy">
                <span>
                  <strong>{completion}%</strong> заполнено
                </span>
                <p>
                  {completion === 100
                    ? 'Анкета заполнена полностью.'
                    : 'Расскажите о себе больше — так проще начать разговор.'}
                </p>
              </div>
              <span className="profile-completion__track" aria-hidden="true">
                <i style={{ width: `${completion}%` }} />
              </span>
            </Link>
          )}
          <div className="profile-primary-actions">
            <SettingsLink to="/profile/edit" icon="edit" label="Редактировать профиль" />
            <SettingsLink to="/profile/photos" icon="photos" label="Фотографии" />
            <SettingsLink to="/typology" icon="ai" label="Мой типаж" />
          </div>
          {profile?.bio || interests.length || details.length ? (
            <section className="owner-profile-details">
              <div className="section-heading">
                <span>О ПРОФИЛЕ</span>
              </div>
              {profile?.bio ? (
                <div className="owner-profile-details__about">
                  <h3>О себе</h3>
                  <p>{profile.bio}</p>
                </div>
              ) : null}
              {interests.length ? (
                <div>
                  <h3>Интересы</h3>
                  <div className="owner-profile-details__chips">
                    {interests.map((item) => (
                      <span key={item}>{item}</span>
                    ))}
                  </div>
                </div>
              ) : null}
              {details.length ? (
                <div className="owner-profile-details__facts">
                  {details.map(([label, value]) => (
                    <div key={label}>
                      <small>{label}</small>
                      <strong>{value}</strong>
                    </div>
                  ))}
                </div>
              ) : null}
            </section>
          ) : (
            <Link className="profile-empty-details" to="/profile/edit">
              <strong>Расскажите о себе</strong>
              <span>Интересы и детали помогут найти общий язык.</span>
            </Link>
          )}
          <section className="settings-group">
            <div className="section-heading">
              <span>НАСТРОЙКИ</span>
            </div>
            <nav className="settings-list settings-list--native">
              <SettingsLink to="/search-settings" icon="search" label="Настроить поиск" />
              <SettingsLink to="/settings" icon="account" label="Все настройки" />
            </nav>
          </section>
        </>
      )}
    </Screen>
  );
}

type OwnerPhoto = {
  id: string;
  assetId: string;
  position: number;
  isPrimary: boolean;
  status: string;
  previewUrl?: OwnerPhotoPreview;
};

export function ProfilePhotosPage() {
  const navigate = useNavigate();
  const profile = useQuery({
    queryKey: ['photo-upload-profile'],
    queryFn: () => api<{ version: number }>('/me/profile'),
  });
  if (profile.isLoading)
    return (
      <Screen title="Фотографии" eyebrow="Моя анкета">
        <AsyncState kind="loading" />
      </Screen>
    );
  if (profile.isError)
    return (
      <Screen title="Фотографии" eyebrow="Моя анкета">
        <AsyncState kind="error" />
      </Screen>
    );
  return (
    <Screen title="Фотографии" eyebrow="Моя анкета" className="screen--profile-photos">
      <p className="lead">
        Покажите себя с разных сторон. Первая готовая фотография становится главной.
      </p>
      <PhotoStep
        version={profile.data!.version}
        manageOnly
        onComplete={() => void navigate('/profile')}
      />
    </Screen>
  );
}

export function SettingsPage() {
  const path = useLocation().pathname;
  if (path.endsWith('/notifications')) return <NotificationSettings />;
  if (path.endsWith('/privacy')) return <PrivacySettings />;
  if (path.endsWith('/ai')) return <AiSettings />;
  if (path.endsWith('/account')) return <AccountSettings />;
  if (path.endsWith('/data')) return <DataSettings />;
  if (path.endsWith('/appearance')) return <AppearanceSettings />;
  return (
    <Screen title="Настройки" className="screen--settings">
      <section className="settings-group">
        <div className="section-heading">
          <span>ЛИЧНОЕ</span>
        </div>
        <nav className="settings-list settings-list--native">
          <SettingsLink
            to="/settings/appearance"
            icon="appearance"
            label="Оформление"
            value="Тема приложения"
          />
        </nav>
      </section>
      <section className="settings-group">
        <div className="section-heading">
          <span>ОБЩЕНИЕ</span>
        </div>
        <nav className="settings-list settings-list--native">
          <SettingsLink
            to="/settings/notifications"
            icon="notifications"
            label="Уведомления"
            value="Сообщения, мэтчи и новости"
          />
          <SettingsLink
            to="/settings/ai"
            icon="ai"
            label="AI-помощник"
            value="Только по вашему действию"
          />
        </nav>
      </section>
      <section className="settings-group">
        <div className="section-heading">
          <span>ПРИВАТНОСТЬ И ПОМОЩЬ</span>
        </div>
        <nav className="settings-list settings-list--native">
          <SettingsLink
            to="/settings/privacy"
            icon="privacy"
            label="Приватность и безопасность"
            value="Блокировки и контроль доступа"
          />
          <SettingsLink
            to="/support"
            icon="support"
            label="Помощь"
            value="Связаться с поддержкой"
          />
          <SettingsLink
            to="/settings/account"
            icon="account"
            label="Аккаунт"
            value="Пауза и удаление"
          />
          <SettingsLink
            to="/settings/data"
            icon="account"
            label="Мои данные"
            value="Выгрузка и выход"
          />
        </nav>
      </section>
    </Screen>
  );
}

function AppearanceSettings() {
  const telegram = getTelegram();
  const [preference, setPreference] = useState<AppearancePreference>(() =>
    readAppearancePreference(),
  );
  const options: { value: AppearancePreference; title: string; description: string }[] = [
    {
      value: 'system',
      title: 'Как в Telegram',
      description: 'Повторяет светлую или тёмную тему Telegram.',
    },
    { value: 'light', title: 'Светлая', description: 'Светлый фон и контрастные карточки.' },
    { value: 'dark', title: 'Тёмная', description: 'Графитовый фон в стиле PONCHIK.' },
  ];
  const select = (value: AppearancePreference) => {
    setPreference(value);
    saveAppearancePreference(value, telegram?.colorScheme);
  };
  return (
    <Screen title="Оформление" eyebrow="Внешний вид" className="screen--settings-detail">
      <p className="lead">Выберите тему PONCHIK независимо от настроек Telegram.</p>
      <div
        className="appearance-options settings-choice-list"
        role="radiogroup"
        aria-label="Тема приложения"
      >
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={preference === option.value}
            className={preference === option.value ? 'is-selected' : ''}
            onClick={() => select(option.value)}
          >
            <span>
              <strong>{option.title}</strong>
              <small>{option.description}</small>
            </span>
            <b aria-hidden="true">{preference === option.value ? '✓' : ''}</b>
          </button>
        ))}
      </div>
    </Screen>
  );
}

export function NotificationSettings() {
  const telegram = getTelegram();
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [accessNotice, setAccessNotice] = useState('');
  const [accessPollUntil, setAccessPollUntil] = useState(0);
  const query = useQuery({
    queryKey: ['notifications'],
    queryFn: () =>
      api<{
        matchEnabled: boolean;
        messageEnabled: boolean;
        productEnabled: boolean;
        writeAccessState: string;
        telegramBotUrl: string;
      }>('/me/notification-preferences'),
    refetchInterval: (query) =>
      query.state.data?.writeAccessState !== 'GRANTED' && Date.now() < accessPollUntil
        ? 2000
        : false,
  });
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: (value: {
      matchEnabled: boolean;
      messageEnabled: boolean;
      productEnabled: boolean;
    }) => api('/me/notification-preferences', { method: 'PUT', body: JSON.stringify(value) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const writeAccess = useMutation({
    mutationFn: (granted: boolean) =>
      api('/me/telegram-write-access/sync', {
        method: 'POST',
        body: JSON.stringify({ granted }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
  if (query.isError)
    return (
      <Screen title="Уведомления" className="screen--settings-detail">
        <AsyncState kind="error" description="Не удалось загрузить настройки уведомлений." />
        <Button tone="secondary" onClick={() => void query.refetch()}>
          Повторить
        </Button>
      </Screen>
    );
  if (!query.data)
    return (
      <Screen title="Уведомления" className="screen--settings-detail">
        <AsyncState kind="loading" />
      </Screen>
    );
  const value = query.data;
  const notificationsEnabled =
    value.writeAccessState === 'GRANTED' && (value.matchEnabled || value.messageEnabled);
  const requestWriteAccess = () => {
    setAccessNotice('');
    setRequestingAccess(true);
    try {
      if (value.writeAccessState !== 'GRANTED') {
        if (localAcceptanceEnabled()) writeAccess.mutate(true);
        else {
          if (!value.telegramBotUrl) throw new Error('BOT_NOT_CONFIGURED');
          if (telegram) telegram.openTelegramLink(value.telegramBotUrl);
          else window.location.assign(value.telegramBotUrl);
          setAccessPollUntil(Date.now() + 60000);
          setAccessNotice('Подтвердите запуск в Telegram.');
        }
      }
      save.mutate({ matchEnabled: true, messageEnabled: true, productEnabled: false });
    } catch {
      setAccessNotice('Не удалось подключить уведомления. Попробуйте ещё раз.');
    } finally {
      setRequestingAccess(false);
    }
  };
  return (
    <Screen title="Уведомления" eyebrow="Общение" className="screen--settings-detail">
      <section className="settings-control-list">
        <div className="settings-control">
          <Toggle
            label="Новые лайки и мэтчи"
            checked={value.matchEnabled}
            onChange={(checked) => {
              save.mutate({ ...value, matchEnabled: checked, productEnabled: false });
            }}
          />
        </div>
        <div className="settings-control">
          <Toggle
            label="Новые сообщения"
            checked={value.messageEnabled}
            onChange={(checked) => {
              save.mutate({ ...value, messageEnabled: checked, productEnabled: false });
            }}
          />
        </div>
      </section>
      <section className="settings-feature-card">
        <span className="settings-feature-card__icon">
          <ProfileSettingsIcon name="notifications" />
        </span>
        <div>
          <h2 role="status">
            {notificationsEnabled ? 'Уведомления включены ✅' : 'Уведомления не подключены'}
          </h2>
          {!notificationsEnabled ? (
            <Button
              type="button"
              onClick={requestWriteAccess}
              disabled={writeAccess.isPending || save.isPending || requestingAccess}
            >
              {writeAccess.isPending || save.isPending || requestingAccess
                ? 'Подключаем…'
                : 'Подключить уведомления'}
            </Button>
          ) : (
            <Button
              tone="secondary"
              disabled={save.isPending}
              onClick={() =>
                save.mutate({ matchEnabled: false, messageEnabled: false, productEnabled: false })
              }
            >
              Отключить уведомления
            </Button>
          )}
          {!notificationsEnabled && accessNotice ? (
            <p role="status" className="hint">
              {accessNotice}
            </p>
          ) : null}
          {writeAccess.isError || save.isError ? (
            <p role="alert">Не удалось сохранить настройки. Попробуйте ещё раз.</p>
          ) : null}
        </div>
      </section>
    </Screen>
  );
}
function PrivacySettings() {
  const query = useQuery({
    queryKey: ['blocks'],
    queryFn: () => api<{ items: { userId: string; displayName: string }[] }>('/me/blocks'),
  });
  const client = useQueryClient();
  const unblock = useMutation({
    mutationFn: (id: string) => api(`/users/${id}/block`, { method: 'DELETE' }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['blocks'] }),
  });
  return (
    <Screen title="Приватность" eyebrow="Безопасность" className="screen--settings-detail">
      <div className="settings-feature-card settings-feature-card--quiet">
        <span className="settings-feature-card__icon">
          <ProfileSettingsIcon name="privacy" />
        </span>
        <div>
          <strong>Заблокированные пользователи</strong>
          <p>
            Они не видят вас и не могут писать. Старый мэтч после разблокировки не восстановится.
          </p>
        </div>
      </div>
      {query.isLoading ? (
        <AsyncState kind="loading" />
      ) : query.isError ? (
        <AsyncState kind="error" description="Не удалось загрузить список блокировок." />
      ) : !query.data?.items.length ? (
        <div className="settings-empty">
          <span>
            <ProfileSettingsIcon name="privacy" />
          </span>
          <strong>Список пуст</strong>
          <p>Здесь появятся пользователи, которых вы заблокировали.</p>
        </div>
      ) : (
        <div className="card-list">
          {query.data.items.map((item) => (
            <article className="compact-card" key={item.userId}>
              <strong>{item.displayName}</strong>
              <Button
                tone="secondary"
                onClick={() => {
                  unblock.mutate(item.userId);
                }}
              >
                Разблокировать
              </Button>
            </article>
          ))}
        </div>
      )}
    </Screen>
  );
}
function AiSettings() {
  const query = useQuery({
    queryKey: ['ai-status'],
    queryFn: () =>
      api<{
        enabled: boolean;
        consent: { assistantUse: boolean; contextSharing: boolean };
        quota: { remaining: number };
      }>('/ai/status'),
  });
  const client = useQueryClient();
  const decide = useMutation({
    mutationFn: (enabled: boolean) =>
      enabled
        ? api('/me/consents', {
            method: 'POST',
            body: JSON.stringify({
              decisions: [{ documentType: 'ai-assistant-use', version: 'v1', granted: true }],
            }),
          })
        : api('/me/consents/assistant-use', {
            method: 'DELETE',
            body: JSON.stringify({ version: 'v1' }),
          }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['ai-status'] }),
  });
  const active = Boolean(query.data?.consent.assistantUse);
  return (
    <Screen title="AI-помощник" eyebrow="Общение" className="screen--settings-detail">
      <div className="settings-feature-card">
        <span className="settings-feature-card__icon">
          <ProfileSettingsIcon name="ai" />
        </span>
        <div>
          <strong>Только предложения</strong>
          <p>
            AI не отправляет сообщения автоматически. Оценка интереса описывает только сигналы в
            переписке и не является фактом о мыслях человека.
          </p>
        </div>
      </div>
      {query.isLoading ? (
        <AsyncState kind="loading" />
      ) : query.isError ? (
        <AsyncState kind="error" description="Не удалось загрузить настройки AI." />
      ) : (
        <>
          <div className="settings-control">
            <Toggle
              label="Разрешить AI-помощник"
              checked={active}
              onChange={(value) => {
                decide.mutate(value);
              }}
            />
            <p>AI работает только когда вы сами запускаете функцию.</p>
          </div>
          <p className="hint">
            AI получает ограниченный контекст только после вашего явного действия. Подсказки не
            отправляются автоматически. Осталось запросов сегодня:{' '}
            {query.data?.quota.remaining ?? 0}.
          </p>
        </>
      )}
    </Screen>
  );
}
export function exportStatusPollInterval(status: string | undefined): number | false {
  return ['REQUESTED', 'PROCESSING'].includes(status ?? '') ? 2_000 : false;
}

export function isNoCurrentExport(error: unknown): boolean {
  return error instanceof ApiClientError && error.code === 'RESOURCE_NOT_FOUND';
}

export function accountVisibilityAction(state: string | undefined): {
  type: 'pause' | 'resume';
  label: string;
} {
  return state === 'PAUSED'
    ? { type: 'resume', label: 'Показать анкету' }
    : { type: 'pause', label: 'Скрыть анкету' };
}

export function isDeletionStatus(state: string | undefined): boolean {
  return state === 'DELETING' || state === 'DELETED';
}

function AccountSettings() {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const account = useQuery({
    queryKey: ['account-settings-me'],
    queryFn: () => api<{ user: { state: string } }>('/me'),
    refetchInterval: (query) => (isDeletionStatus(query.state.data?.user.state) ? 2000 : false),
  });
  const deletion = useQuery({
    queryKey: ['deletion-status'],
    queryFn: async () => {
      try {
        return await api<{ irreversible: boolean; status: string; mode: string }>(
          '/me/deletion-requests/current',
        );
      } catch (error) {
        if (isNoCurrentExport(error)) return null;
        throw error;
      }
    },
    retry: false,
  });
  const deleting =
    isDeletionStatus(account.data?.user.state) ||
    ['REQUESTED', 'PROCESSING'].includes(deletion.data?.status ?? '');
  const visibility = accountVisibilityAction(account.data?.user.state);
  const action = async (type: 'pause' | 'resume' | 'delete') => {
    if (busy) return;
    setBusy(true);
    try {
      if (type === 'delete') {
        if (deleteConfirmation !== 'DELETE') return;
        useSession.getState().beginDeletion();
        await api('/me/deletion-requests', {
          method: 'POST',
          body: JSON.stringify({ confirmation: 'DELETE', mode: 'FULL', irreversible: true }),
        });
        saveAppearancePreference('system', getTelegram()?.colorScheme);
        useSession.getState().acceptDeletion();
        setSessionToken(null);
        clearLocalAcceptanceSession();
        await queryClient.cancelQueries();
        queryClient.clear();
        return;
      } else {
        await api(`/me/${type}`, { method: 'POST', body: '{}' });
        setMessage(
          type === 'pause'
            ? 'Анкета скрыта из знакомств. Все остальные возможности доступны.'
            : 'Показ анкеты включён.',
        );
      }
      await account.refetch();
    } catch (error) {
      if (type === 'delete' && useSession.getState().deletionPhase === 'REQUESTING')
        useSession.getState().failDeletion();
      setMessage(error instanceof Error ? error.message : 'Не удалось выполнить действие');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Screen
      title="Аккаунт"
      eyebrow="Данные и доступ"
      className="screen--settings-detail screen--account-settings"
    >
      {deleting ? (
        <div className="notice">
          <span>
            {deletion.data?.irreversible
              ? 'Удаляем аккаунт. Это может занять немного времени. После завершения откроется новая регистрация.'
              : 'У вас есть ранее оформленный запрос на удаление. Его прежние условия сохранены.'}
          </span>
          {deletion.data && !deletion.data.irreversible && deletion.data.status === 'REQUESTED' && (
            <Button
              tone="secondary"
              onClick={() =>
                void api('/me/deletion-requests/current/restore', {
                  method: 'POST',
                  body: '{}',
                })
                  .then(() => account.refetch())
                  .catch((error: unknown) =>
                    setMessage(error instanceof Error ? error.message : 'Не удалось восстановить'),
                  )
              }
            >
              Отменить прежний запрос
            </Button>
          )}
        </div>
      ) : (
        <div className="account-action-list">
          <Button
            tone="secondary"
            disabled={busy || !account.data}
            onClick={() => void action(visibility.type)}
          >
            {visibility.label}
          </Button>
          <Button
            tone="danger"
            disabled={busy || !account.data}
            onClick={() => setDeleteOpen(true)}
          >
            Удалить аккаунт
          </Button>
        </div>
      )}
      {(message || account.error) && (
        <div className="notice">
          <span>
            {message ||
              'Не удалось загрузить состояние аккаунта. Попробуйте открыть приложение заново.'}
          </span>
        </div>
      )}
      {deleteOpen && (
        <section
          className="confirm-sheet"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-account-title"
        >
          <h2 id="delete-account-title">Удалить аккаунт?</h2>
          <p>
            Все данные аккаунта будут удалены без возможности восстановления. Переписки удалятся у
            вас и у собеседников. Для подтверждения введите DELETE.
          </p>
          <Field label="Подтверждение">
            <Input
              value={deleteConfirmation}
              onChange={(event) => setDeleteConfirmation(event.target.value)}
              autoComplete="off"
            />
          </Field>
          <div className="confirm-sheet__actions">
            <Button
              tone="secondary"
              disabled={busy}
              onClick={() => {
                setDeleteOpen(false);
                setDeleteConfirmation('');
              }}
            >
              Отмена
            </Button>
            <Button
              tone="danger"
              disabled={busy || deleteConfirmation !== 'DELETE'}
              onClick={() => void action('delete')}
            >
              {busy ? 'Удаляем…' : 'Удалить аккаунт'}
            </Button>
          </div>
        </section>
      )}
    </Screen>
  );
}

function DataSettings() {
  const navigate = useNavigate();
  const clearSession = useSession((state) => state.clear);
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');
  const status = useQuery({
    queryKey: ['export-status'],
    queryFn: async () => {
      try {
        return await api<{ status: string; downloadUrl?: string }>(
          '/me/data-export-requests/current',
        );
      } catch (error) {
        if (isNoCurrentExport(error)) return null;
        throw error;
      }
    },
    retry: false,
    refetchInterval: (query) => exportStatusPollInterval(query.state.data?.status),
  });
  return (
    <Screen title="Мои данные" eyebrow="Данные и доступ">
      <div className="account-action-list">
        <Button
          tone="secondary"
          onClick={() =>
            void api('/me/data-export-requests', { method: 'POST', body: '{}' })
              .then(async () => {
                await status.refetch();
                setMessage('Выгрузка готовится.');
              })
              .catch((error: unknown) =>
                setMessage(
                  error instanceof Error ? error.message : 'Не удалось запросить выгрузку',
                ),
              )
          }
        >
          Запросить выгрузку данных
        </Button>
        {status.data?.downloadUrl && (
          <a className="button button--secondary" href={status.data.downloadUrl}>
            Скачать готовую выгрузку
          </a>
        )}
        <Button
          tone="secondary"
          onClick={() =>
            void logoutTelegram()
              .then(() => {
                clearSession();
                queryClient.clear();
                void navigate('/signed-out', { replace: true });
              })
              .catch(() => setMessage('Не удалось выйти. Повторите попытку.'))
          }
        >
          Выйти
        </Button>
      </div>
      {(message || status.error) && (
        <p className="notice">{message || 'Не удалось получить состояние выгрузки.'}</p>
      )}
    </Screen>
  );
}

export function SignedOutPage() {
  const telegram = getTelegram();
  return (
    <Screen title="Вы вышли" eyebrow="Сессия закрыта">
      <p className="lead">
        Текущая сессия отозвана. Для нового входа снова откройте Mini App в Telegram.
      </p>
      <Button onClick={() => telegram?.close()}>Закрыть Mini App</Button>
    </Screen>
  );
}

export function LegalPage() {
  const { document = 'terms' } = useParams();
  const { search } = useLocation();
  const version = new URLSearchParams(search).get('version');
  const query = useQuery({
    queryKey: ['legal', document, version],
    queryFn: () =>
      api<{ title: string; version: string; body: string }>(
        `/legal/documents?${new URLSearchParams({ type: document, ...(version !== null ? { version } : {}) })}`,
      ),
  });
  const eyebrow = query.data?.version ? `Версия ${query.data.version}` : null;
  return (
    <Screen title={query.data?.title ?? 'Правовой документ'} {...(eyebrow ? { eyebrow } : {})}>
      {query.isLoading ? (
        <AsyncState kind="loading" />
      ) : (
        <article className="legal-copy">
          <LegalDocumentBody
            body={
              query.data?.body ??
              'Документ временно недоступен. Продолжение регистрации заблокировано до восстановления.'
            }
          />
        </article>
      )}
    </Screen>
  );
}
export function SupportPage() {
  return (
    <Screen title="Помощь" eyebrow="Мы рядом" className="screen--settings-detail screen--support">
      <section className="support-card">
        <span className="support-card__icon">
          <ProfileSettingsIcon name="support" />
        </span>
        <h2>Нужна помощь?</h2>
        <p>Напишите удобным способом — ответим бережно и без лишних данных.</p>
        <a
          className="button button--primary"
          href="https://t.me/ponchik_support"
          target="_blank"
          rel="noreferrer"
        >
          Написать в Telegram
        </a>
        <a
          className="support-email"
          href="https://t.me/ponchik_support"
          target="_blank"
          rel="noreferrer"
        >
          @ponchik_support
        </a>
        <a className="support-email" href="mailto:support@ponchikapp.com">
          support@ponchikapp.com
        </a>
      </section>
    </Screen>
  );
}
export function NotFoundPage() {
  return (
    <Screen title="Такой страницы нет">
      <p className="lead">Возможно, ссылка устарела или доступ к разделу изменился.</p>
      <Link className="button button--primary" to="/discover">
        Вернуться к знакомствам
      </Link>
    </Screen>
  );
}
